//comment to trigger build
import React, { useState } from 'react';
import { Container, Tabs, Tab, Form, Button, ListGroup, Row, Col, OverlayTrigger, Tooltip, Modal, Table, Toast, ToastContainer, Dropdown, ButtonGroup, Card, Alert } from 'react-bootstrap';
import HelpChatIcon from '../components/HelpChatIcon';
import UsageChart from '../components/charts/UsageChart';
import { supabase } from '../lib/supabaseClient';
import { debugLog } from '../lib/debugLog';
import { useAuth } from '../auth/AuthContext';
import { useSettings } from '../settings/SettingsContext';
import { useTenant } from '../settings/TenantContext';
import { useNavigate } from 'react-router-dom';
import { edgeFetch } from '../lib/edgeFetch';
import AutoLaunchModal from '../components/theme/AutoLaunchModal';
import QuickRepliesModal from '../components/theme/QuickRepliesModal';
import CtaModal from '../components/theme/CtaModal';
import AddSiteModal from '../components/sites/AddSiteModal.jsx';
const ThemeEditorModal = React.lazy(() => import('../components/theme/ThemeEditorModal'));
const edgeBase = process.env.REACT_APP_SKINNY_SUPABASE_URL;

function UserAnalysisTab() {
  const { tenantId, siteId } = useTenant();

  const [startLocal, setStartLocal] = React.useState('');
  const [endLocal, setEndLocal] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [report, setReport] = React.useState(null);
 

  function parseYmd(dateStr) { try { const [y, m, d] = String(dateStr||'').split('-').map(Number); return (y&&m&&d) ? { y, m, d } : null; } catch (_) { return null; } }
  function dateOnlyToUtcIsoStart(v) { const p = parseYmd(v); if (!p) return ''; try { return new Date(Date.UTC(p.y, p.m-1, p.d, 0, 0, 0)).toISOString(); } catch (_) { return ''; } }
  function dateOnlyToUtcIsoEnd(v) { const p = parseYmd(v); if (!p) return ''; try { return new Date(Date.UTC(p.y, p.m-1, p.d, 23, 59, 0)).toISOString(); } catch (_) { return ''; } }

  function formatLocalTs(ts) { try { return ts ? new Date(ts).toLocaleString() : ''; } catch (_) { return String(ts||''); } }
  function getQaRequestText(e) { try { return (e?.request?.text) || e?.snippet || ''; } catch(_) { return ''; } }
  function unwrapJsonResponse(raw) {
    const s = String(raw||'').trim();
    if (!s) return '';
    if (s.charAt(0) !== '{') return s;
    try {
      const obj = JSON.parse(s);
      if (obj?.response?.text) return obj.response.text;
      if (obj?.text) return obj.text;
    } catch (_) {
      const m = s.match(/"text"\s*:\s*"((?:[^"\\]|\\.)*)("|\s*$)/);
      if (m) { try { return JSON.parse('"' + m[1] + '"'); } catch (_) { return m[1].replace(/\\"/g, '"').replace(/\\n/g, '\n'); } }
    }
    return s;
  }
  function getQaResponse(e) {
    try { return unwrapJsonResponse((e?.response?.text) || ''); } catch (_) { return ''; }
  }

  function getQaDate(e) { const ts = e?.response?.ts || e?.request?.ts || ''; return formatLocalTs(ts); }

  // Downloads (JSON/RTF/Print PDF)
  function downloadBlob(filename, mime, content) {
    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; document.body.appendChild(a); a.click();
    setTimeout(()=>{ URL.revokeObjectURL(url); document.body.removeChild(a); }, 0);
  }
  function escapeRtf(text) {
    return String(text || '')
      .replace(/\\/g, '\\\\')
      .replace(/\{/g, '\\{')
      .replace(/\}/g, '\\}')
      .replace(/\n/g, '\\line ');
  }
  function buildFileBase() {
    const tenant = (tenantId || 'mytenant').replace(/[^a-z0-9_-]+/gi, '-');
    const d = new Date();
    const ymd = `${d.getUTCFullYear()}${String(d.getUTCMonth()+1).padStart(2,'0')}${String(d.getUTCDate()).padStart(2,'0')}`;
    const start = startLocal ? dateOnlyToUtcIsoStart(startLocal).slice(0,19).replace(/[:T]/g,'') : 'start';
    const end = endLocal ? dateOnlyToUtcIsoEnd(endLocal).slice(0,19).replace(/[:T]/g,'') : 'end';
    return `analysis_${tenant}_${ymd}_${start}-${end}`;
  }
  function buildRtfFromReport(rep) {
    const parts = [];
    parts.push('{\\rtf1\\ansi\\deff0');
    const header = `TheSkinnyAI Analysis Report\\line Tenant: ${escapeRtf(tenantId||'tenant')}\\line Timeframe: ${escapeRtf(rep?.timeframe?.start_iso||'')} to ${escapeRtf(rep?.timeframe?.end_iso||'')} (${escapeRtf(rep?.timeframe?.timezone||'UTC')})\\line \\line`;
    parts.push(header);
    if (rep?.summary) parts.push('\\b Summary \\b0\\line ' + escapeRtf(rep.summary) + '\\line \\line ');
    parts.push('\\b Totals \\b0\\line ');
    parts.push(`Sessions: ${rep?.totals?.sessions??0}\\line Messages: ${rep?.totals?.messages??0}\\line Avg Minutes: ${typeof rep?.totals?.avg_session_minutes==='number'?rep.totals.avg_session_minutes.toFixed(1):'—'}\\line Avg Exchanges/Session: ${typeof rep?.totals?.avg_exchanges_per_session==='number'?rep.totals.avg_exchanges_per_session.toFixed(1):'—'}\\line \\line `);
    parts.push('\\b Trends (Topics) \\b0\\line ');
    (rep?.trends?.topics||[]).slice(0,50).forEach(t => {
      const pct = typeof t.percent==='number' ? (t.percent*100).toFixed(1)+'%' : '—';
      parts.push(`${escapeRtf(t.name)}: ${t.count??0} (${pct})\\line `);
    });
    parts.push('\\line ');
    parts.push('\\b Personas \\b0\\line ');
    (rep?.personas||[]).slice(0,50).forEach(p => {
      const pct = typeof p.percent==='number' ? (p.percent*100).toFixed(1)+'%' : '—';
      parts.push(`${escapeRtf(p.name)}: ${p.count??0} (${pct})\\line `);
    });
    parts.push('\\line ');
    parts.push('\\b Funnel \\b0\\line Steps\\line ');
    (rep?.funnel?.steps||[]).forEach(s => parts.push(`${escapeRtf(s.label||s.id)}: ${s.count??0}\\line `));
    parts.push('Drop-off\\line ');
    (rep?.funnel?.dropoff||[]).forEach(d => {
      const rate = typeof d.rate==='number' ? (d.rate*100).toFixed(1)+'%' : '—';
      parts.push(`${escapeRtf(d.from_id)} → ${escapeRtf(d.to_id)}: ${rate}\\line `);
    });
    parts.push('\\line ');
    parts.push('\\b Sentiment (Overall) \\b0\\line ');
    if (rep?.sentiment?.overall) {
      const o = rep.sentiment.overall;
      const pos = typeof o.positive==='number' ? (o.positive*100).toFixed(0)+'%' : '—';
      const neu = typeof o.neutral==='number' ? (o.neutral*100).toFixed(0)+'%' : '—';
      const neg = typeof o.negative==='number' ? (o.negative*100).toFixed(0)+'%' : '—';
      parts.push(`Positive: ${pos}, Neutral: ${neu}, Negative: ${neg}\\line `);
    }
    parts.push('\\line ');
    parts.push('\\b QA Flags \\b0\\line ');
    if (rep?.qa_flags) {
      const c = (rep.qa_flags.concerning_responses_count ?? 0);
      parts.push(`Concerning Responses: ${c}\\line `);
      const ex = Array.isArray(rep.qa_flags.examples) ? rep.qa_flags.examples : [];
      ex.slice(0, 20).forEach(e => {
        const date = getQaDate(e);
        const req = getQaRequestText(e);
        const res = getQaResponse(e);
        if (date) parts.push(`Date: ${escapeRtf(date)}\\line `);
        if (req) parts.push(`Request: ${escapeRtf(req)}\\line `);
        if (res) parts.push(`Response: ${escapeRtf(res)}\\line `);
        parts.push('\\line ');
      });
    }
    parts.push('\\b Insights \\b0\\line ');
    (rep?.insights||[]).forEach(ins => { parts.push(`• ${escapeRtf(ins.title||'')}\\line ${escapeRtf(ins.summary||'')}\\line Recommendation: ${escapeRtf(ins.recommendation||'')}\\line \\line `); });
    parts.push('}');
    return parts.join('');
  }
  function openPrintView(rep) {
    const w = window.open('', '_blank'); if (!w) return;
    const tenantName = tenantId || 'My Tenant';
    const html = `<!doctype html><html><head><meta charset="utf-8"/><title>TheSkinnyAI Analysis Report</title>
    <style>body{font-family:system-ui,-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;padding:24px;}
    h1{font-size:20px;margin:0 0 8px;} h2{font-size:16px;margin:16px 0 8px;} .muted{color:#666;font-size:12px}
    table{border-collapse:collapse;width:100%;} td,th{border:1px solid #ddd;padding:6px;font-size:12px}
    </style></head><body>
    <h1>TheSkinnyAI Analysis Report</h1>
    <div class="muted">Tenant: ${tenantName}</div>
    <div class="muted">Timeframe: ${rep?.timeframe?.start_iso||''} → ${rep?.timeframe?.end_iso||''} (${rep?.timeframe?.timezone||'UTC'})</div>
    ${rep?.summary ? `<h2>Summary</h2><div>${rep.summary}</div>` : ''}
    <h2>Totals</h2>
    <div>Sessions: ${rep?.totals?.sessions??0} &nbsp; Messages: ${rep?.totals?.messages??0} &nbsp; Avg Minutes: ${typeof rep?.totals?.avg_session_minutes==='number'?rep.totals.avg_session_minutes.toFixed(1):'—'} &nbsp; Avg Exchanges/Session: ${typeof rep?.totals?.avg_exchanges_per_session==='number'?rep.totals.avg_exchanges_per_session.toFixed(1):'—'}</div>
    <h2>Trends (Topics)</h2>
    <table><thead><tr><th>Topic</th><th>Count</th><th>%</th></tr></thead><tbody>
    ${(rep?.trends?.topics||[]).map(t=>`<tr><td>${t.name}</td><td>${t.count??0}</td><td>${typeof t.percent==='number'?(t.percent*100).toFixed(1)+'%':'—'}</td></tr>`).join('')}
    </tbody></table>
    <h2>Personas</h2>
    <table><thead><tr><th>Persona</th><th>Count</th><th>%</th></tr></thead><tbody>
    ${(rep?.personas||[]).map(p=>`<tr><td>${p.name}</td><td>${p.count??0}</td><td>${typeof p.percent==='number'?(p.percent*100).toFixed(1)+'%':'—'}</td></tr>`).join('')}
    </tbody></table>
    <h2>Funnel</h2>
    <h3>Steps</h3>
    <table><thead><tr><th>Step</th><th>Count</th></tr></thead><tbody>
    ${(rep?.funnel?.steps||[]).map(s=>`<tr><td>${s.label||s.id}</td><td>${s.count??0}</td></tr>`).join('')}
    </tbody></table>
    <h3>Drop-off</h3>
    <table><thead><tr><th>From → To</th><th>Rate</th></tr></thead><tbody>
    ${(rep?.funnel?.dropoff||[]).map(d=>`<tr><td>${d.from_id} → ${d.to_id}</td><td>${typeof d.rate==='number'?(d.rate*100).toFixed(1)+'%':'—'}</td></tr>`).join('')}
    </tbody></table>
    <h2>Sentiment (Overall)</h2>
    <div class="muted">${rep?.sentiment?.overall?`${(rep.sentiment.overall.positive*100||0).toFixed(0)}% positive · ${(rep.sentiment.overall.neutral*100||0).toFixed(0)}% neutral · ${(rep.sentiment.overall.negative*100||0).toFixed(0)}% negative`:''}</div>
    <h2>QA Flags</h2>
    ${rep?.qa_flags ? `<div>Concerning Responses: ${rep.qa_flags.concerning_responses_count ?? 0}</div>` : ''}
    ${rep?.qa_flags && Array.isArray(rep.qa_flags.examples) && rep.qa_flags.examples.length ? `
      <table><thead><tr><th>Date</th><th>Request</th><th>Response</th></tr></thead><tbody>
      `+rep.qa_flags.examples.slice(0,20).map(e=>{
        const fmt = (ts)=>{ try { return ts ? new Date(ts).toLocaleString() : ''; } catch(_) { return ts||''; } };
        const date = e?.response?.ts ? fmt(e.response.ts) : (e?.request?.ts ? fmt(e.request.ts) : '');
        const req = (e?.request && typeof e.request.text==='string') ? e.request.text : (typeof e?.snippet==='string' ? e.snippet : '');
        let res = unwrapJsonResponse((e?.response && typeof e.response.text==='string') ? e.response.text : '');
        return `<tr><td>${date||''}</td><td>${req||''}</td><td>${res||''}</td></tr>`;
      }).join('')+`</tbody></table>
    ` : ''}
    <h2>Insights</h2>
    ${(rep?.insights||[]).map(ins=>`<div><strong>${ins.title||''}</strong><div>${ins.summary||''}</div><div class="muted">Recommendation: ${ins.recommendation||''}</div></div>`).join('')}
    <script>window.onload=()=>{window.print()};</script>
    </body></html>`;
    w.document.open(); w.document.write(html); w.document.close();
  }

  async function fetchAnalysis() {
    if (!tenantId) { alert('Tenant not loaded'); return; }
    if (!startLocal || !endLocal) { alert('Select start and end dates'); return; }
    const startIso = dateOnlyToUtcIsoStart(startLocal);
    const endIso = dateOnlyToUtcIsoEnd(endLocal);
    if (!startIso || !endIso) { alert('Invalid dates'); return; }
    setLoading(true); setReport(null);
    try {
      let token = (await supabase.auth.getSession()).data.session?.access_token || '';
      for (let i = 0; !token && i < 10; i++) { await new Promise(r=>setTimeout(r,150)); token = (await supabase.auth.getSession()).data.session?.access_token || ''; }
      const selectedSiteId = siteId || ((typeof window !== 'undefined' && tenantId) ? (localStorage.getItem(`site_sel_${tenantId}`) || '') : '');
      const body = { tenant_id: tenantId, site_id: selectedSiteId || undefined, start_iso: startIso, end_iso: endIso, timezone: 'UTC', exclude_short_sessions: true, max_sessions: 100, max_messages_per_session: 30 };
      const url = `${edgeBase}/functions/v1/analysis-generate`;
      debugLog('[UserAnalysis] fetch', { url, tenantId, siteId: selectedSiteId, hasToken: !!token });
      const res = await fetch(url, { method: 'POST', headers: { 'content-type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(body) });
      const txt = await res.text();
      if (!res.ok) {
        debugLog('[UserAnalysis] error', { status: res.status, body: txt });
        try {
          const j = JSON.parse(txt);
          if (j && j.error === 'analysis_disabled') { alert('Not available at your subscription level.'); return; }
        } catch (_) {}
        alert(`Error ${res.status}`);
        return;
      }
      let json = {}; try { json = JSON.parse(txt); } catch (_) { alert('Invalid response'); return; }
      const rep = json && json.report ? json.report : json;
      try {
        if (rep?.trends?.topics?.length) rep.trends.topics = rep.trends.topics.map((t)=>({ ...t, percent: (typeof t.percent==='number' && t.percent>1)?(t.percent/100):t.percent }));
        if (rep?.funnel?.dropoff?.length) rep.funnel.dropoff = rep.funnel.dropoff.map((d)=>({ ...d, rate: (typeof d.rate==='number' && d.rate>1)?(d.rate/100):d.rate }));
      } catch(_) {}
      setReport(rep);
    } catch (e) { alert(String(e?.message||e)); }
    setLoading(false);
  }

  return (
    <div>
      <Row className="g-3 align-items-end mb-3">
        <Col md>
          <Form.Label>Start Date (UTC)</Form.Label>
          <Form.Control type="date" value={startLocal} onChange={(e)=>setStartLocal(e.target.value)} />
        </Col>
        <Col md>
          <Form.Label>End Date (UTC)</Form.Label>
          <Form.Control type="date" value={endLocal} onChange={(e)=>setEndLocal(e.target.value)} />
        </Col>
        <Col md="auto" className="pt-4">
          <Button onClick={fetchAnalysis} disabled={loading || !tenantId || !startLocal || !endLocal}>{loading ? 'Fetching…' : 'Fetch Analysis'}</Button>
        </Col>
        <Col md="auto" className="pt-4">
          <Dropdown as={ButtonGroup}>
            <Button variant="outline-secondary" disabled={!report} onClick={() => { if (!report) return; const base = buildFileBase(); downloadBlob(base + '.json', 'application/json', JSON.stringify(report, null, 2)); }}>Download</Button>
            <Dropdown.Toggle split variant="outline-secondary" id="user-an-download" disabled={!report} />
            <Dropdown.Menu>
              <Dropdown.Item disabled={!report} onClick={() => { if (!report) return; const base = buildFileBase(); downloadBlob(base + '.json', 'application/json', JSON.stringify(report, null, 2)); }}>JSON</Dropdown.Item>
              <Dropdown.Item disabled={!report} onClick={() => { if (!report) return; const base = buildFileBase(); const rtf = buildRtfFromReport(report); downloadBlob(base + '.rtf', 'application/rtf', rtf); }}>RTF</Dropdown.Item>
              <Dropdown.Item disabled={!report} onClick={() => { if (report) openPrintView(report); }}>Print (PDF)</Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </Col>
      </Row>

      {report && (
        <div className="mt-3">
          <div className="border rounded p-3 bg-light mb-3">
            <div className="fw-semibold mb-2 d-flex align-items-center">
              <span>Overview</span>
            </div>
            <div className="small text-muted">Timeframe</div>
            <div className="mb-2 text-monospace small">{report?.timeframe?.start_iso} → {report?.timeframe?.end_iso} ({report?.timeframe?.timezone || 'UTC'})</div>
            <div className="small text-muted">Summary</div>
            <div>{report?.summary || '—'}</div>
            <Row className="g-3 mt-2">
              <Col md>
                <div className="small text-muted">Sessions</div>
                <div>{report?.totals?.sessions ?? 0}</div>
              </Col>
              <Col md>
                <div className="small text-muted">Messages</div>
                <div>{report?.totals?.messages ?? 0}</div>
              </Col>
              <Col md>
                <div className="small text-muted">Avg Minutes</div>
                <div>{typeof report?.totals?.avg_session_minutes === 'number' ? report.totals.avg_session_minutes.toFixed(1) : '—'}</div>
              </Col>
              <Col md>
                <div className="small text-muted">Avg Exchanges/Session</div>
                <div>{typeof report?.totals?.avg_exchanges_per_session === 'number' ? report.totals.avg_exchanges_per_session.toFixed(1) : '—'}</div>
              </Col>
            </Row>
          </div>

          <div className="border rounded p-3 bg-white mb-3">
            <div className="fw-semibold mb-2 d-flex align-items-center">
              <span>Trends (Topics)</span>
            </div>
            <Table size="sm" responsive>
              <thead><tr><th>Topic</th><th>Count</th><th>%</th></tr></thead>
              <tbody>
                {(report?.trends?.topics || []).slice().sort((a,b)=>(b.count??0)-(a.count??0)).map((t,i)=>(
                  <tr key={i}><td>{t.name}</td><td>{t.count ?? 0}</td><td>{typeof t.percent==='number' ? (t.percent*100).toFixed(1)+'%' : '—'}</td></tr>
                ))}
                {(!report?.trends?.topics || report.trends.topics.length===0) && (<tr><td colSpan={3} className="text-muted small">No topics</td></tr>)}
              </tbody>
            </Table>
          </div>

          <div className="border rounded p-3 bg-light mb-3">
            <div className="fw-semibold mb-2 d-flex align-items-center"><span>Sentiment (Overall)</span></div>
            <div className="text-monospace small">
              {typeof report?.sentiment?.overall?.positive==='number' ? (report.sentiment.overall.positive*100).toFixed(0)+'% positive' : '—'} · {typeof report?.sentiment?.overall?.neutral==='number' ? (report.sentiment.overall.neutral*100).toFixed(0)+'% neutral' : '—'} · {typeof report?.sentiment?.overall?.negative==='number' ? (report.sentiment.overall.negative*100).toFixed(0)+'% negative' : '—'}
            </div>
          </div>

          <div className="border rounded p-3 bg-white mb-3">
            <div className="fw-semibold mb-2 d-flex align-items-center"><span>Personas</span></div>
            <Table size="sm" responsive>
              <thead><tr><th>Persona</th><th>Count</th><th>%</th></tr></thead>
              <tbody>
                {(report?.personas||[]).map((p,i)=>(<tr key={i}><td>{p.name}</td><td>{p.count??0}</td><td>{typeof p.percent==='number'?(p.percent*100).toFixed(1)+'%':'—'}</td></tr>))}
                {(!report?.personas || report.personas.length===0) && (<tr><td colSpan={3} className="text-muted small">No personas</td></tr>)}
              </tbody>
            </Table>
          </div>

          <div className="border rounded p-3 bg-light mb-3">
            <div className="fw-semibold mb-2 d-flex align-items-center"><span>Funnel</span></div>
            <Row className="g-3">
              <Col md>
                <div className="small text-muted mb-1">Steps</div>
                <Table size="sm" responsive>
                  <thead><tr><th>Step</th><th>Count</th></tr></thead>
                  <tbody>
                    {(report?.funnel?.steps||[]).map((s,i)=>(<tr key={i}><td>{s.label||s.id}</td><td>{s.count??0}</td></tr>))}
                    {(!report?.funnel?.steps || report.funnel.steps.length===0) && (<tr><td colSpan={2} className="text-muted small">No steps</td></tr>)}
                  </tbody>
                </Table>
              </Col>
              <Col md>
                <div className="small text-muted mb-1">Drop-off</div>
                <Table size="sm" responsive>
                  <thead><tr><th>From → To</th><th>Rate</th></tr></thead>
                  <tbody>
                    {(report?.funnel?.dropoff||[]).map((d,i)=>(<tr key={i}><td>{d.from_id} → {d.to_id}</td><td>{typeof d.rate==='number'?(d.rate*100).toFixed(1)+'%':'—'}</td></tr>))}
                    {(!report?.funnel?.dropoff || report.funnel.dropoff.length===0) && (<tr><td colSpan={2} className="text-muted small">No drop-off data</td></tr>)}
                  </tbody>
                </Table>
              </Col>
            </Row>
          </div>

          <div className="border rounded p-3 bg-white mb-3">
            <div className="fw-semibold mb-2 d-flex align-items-center"><span>QA Flags</span></div>
            {report?.qa_flags?.concerning_responses_count ? (
              <div className="mb-2">Concerning responses: {report.qa_flags.concerning_responses_count}</div>
            ) : (
              <div className="mb-2">Concerning responses: 0</div>
            )}
            <Table size="sm" responsive>
              <thead><tr><th>Session</th><th>Date</th><th>Reason</th><th>Request</th><th>Response</th></tr></thead>
              <tbody>
                {(report?.qa_flags?.examples||[]).map((q,i)=>(
                  <tr key={i}>
                    <td className="text-monospace small" style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>{q.session_id}</td>
                    <td className="small">{getQaDate(q) || '—'}</td>
                    <td className="small">{q.reason || '—'}</td>
                    <td className="small" style={{ maxWidth: 320, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{getQaRequestText(q) || '—'}</td>
                    <td className="small" style={{ maxWidth: 320, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{getQaResponse(q) || '—'}</td>
                  </tr>
                ))}
                {(!report?.qa_flags?.examples || report.qa_flags.examples.length===0) && (<tr><td colSpan={5} className="text-muted small">No examples</td></tr>)}
              </tbody>
            </Table>
          </div>

          <div className="border rounded p-3 bg-light mb-3">
            <div className="fw-semibold mb-2 d-flex align-items-center"><span>Insights</span></div>
            {(report?.insights||[]).map((ins,i)=>(
              <div key={i} className="mb-2">
                <div className="fw-semibold">{ins.title}</div>
                <div className="small">{ins.summary}</div>
                <div className="small text-muted">Recommendation: {ins.recommendation}</div>
              </div>
            ))}
            {(!report?.insights || report.insights.length===0) && (<div className="text-muted small">No insights</div>)}
          </div>
        </div>
      )}
    </div>
  );
}

function SettingsTab() {
  const { tenantName, setTenantName, siteUrl, setSiteUrl, startingUrl, setStartingUrl, doNotCrawlUrls, addDoNotCrawl, updateDoNotCrawl, removeDoNotCrawl, useSitemapSeed, setUseSitemapSeed, sitemapUrl, setSitemapUrl, emailNotificationsEnabled, setEmailNotificationsEnabled, notificationRecipients, setNotificationRecipients, serverEmailNotificationsEnabled, serverNotificationRecipients } = useSettings();
  const { tenantId } = useTenant();
  const [newUrl, setNewUrl] = useState('');
  // Removed Supabase test utilities

  // (InfoIcon removed; HelpChatIcon used instead)

  return (
    <div>
      <Form.Group className="mb-3">
        <Form.Label>Tenant Name</Form.Label>
        <Form.Control type="text" value={tenantName || ''} onChange={(e) => setTenantName(e.target.value)} />
        <div className="form-text">This name is shown in your workspace selector. It does not affect your site URL or billing.</div>
      </Form.Group>
      <Form.Group className="mb-3">
        <Form.Label>Site URL</Form.Label>
        <Form.Control type="url" value={siteUrl} onChange={(e) => setSiteUrl(e.target.value)} />
        <div className="form-text">After changing your Site URL, you must request a new crawl. Shares and embeds won't update until re-crawled.</div>
      </Form.Group>
      <Form.Group className="mb-4">
        <Form.Label>
          Starting URL
          <HelpChatIcon className="ms-1" title="The first page to crawl. Usually your homepage or a docs/FAQ index. You can change this later." seedQuestion="What is the starting url?" />
        </Form.Label>
        <Form.Control type="url" value={startingUrl} onChange={(e) => setStartingUrl(e.target.value)} />
        <Form.Check className="mt-2" type="checkbox" id="dash-use-sitemap-seed"
          label={
            <>
              Use sitemap.xml as initial seed
              <HelpChatIcon
                className="ms-1"
                title="When checked, we start with the pages listed in sitemap.xml, If we don't find this file on your site, we start with the Starting URL. If sitemap.xml does not contain your starting URL, we will add both to our crawl."
                seedQuestion="Why enable use sitemap.xml as initial seed?"
              />
            </>
          }
          checked={!!useSitemapSeed}
          onChange={(e) => setUseSitemapSeed(e.target.checked)}
        />
        <Form.Control className="mt-2" type="url" placeholder="Custom sitemap URL (optional)" value={sitemapUrl || ''} onChange={(e)=> setSitemapUrl(e.target.value)} />
      </Form.Group>
      <Form.Group className="mb-2">
        <Form.Label>
          Do Not Crawl URLs
          <HelpChatIcon
            className="ms-1"
            title="Exclude paths or full URLs from discovery/crawling (e.g., /admin, /privacy). Add one at a time and edit inline."
            seedQuestion="What are Do Not Crawl URLs?"
          />
        </Form.Label>
        <Row className="g-2 align-items-center mb-2">
          <Col>
            <Form.Control type="url" placeholder="https://example.com/path" value={newUrl} onChange={(e) => setNewUrl(e.target.value)} />
          </Col>
          <Col xs="auto">
            <Button onClick={() => { if (newUrl.trim()) { addDoNotCrawl(newUrl.trim()); setNewUrl(''); } }}>Add</Button>
          </Col>
        </Row>
        <ListGroup>
          {doNotCrawlUrls.map((u, i) => (
            <ListGroup.Item key={`${u}-${i}`} className="d-flex align-items-center justify-content-between">
              <Form.Control
                type="url"
                value={u}
                onChange={(e) => updateDoNotCrawl(i, e.target.value)}
              />
              <Button variant="outline-danger" className="ms-2" onClick={() => removeDoNotCrawl(i)}>Remove</Button>
            </ListGroup.Item>
          ))}
          {doNotCrawlUrls.length === 0 && (
            <ListGroup.Item className="text-muted">No exclusions</ListGroup.Item>
          )}
        </ListGroup>
      </Form.Group>
      <div className="border rounded p-3 bg-light mt-4">
        <div className="fw-semibold mb-2">Email Notifications</div>
        <Form.Check
          type="switch"
          id="dash-email-notifs"
          label="Email me when a contact is captured"
          checked={!!emailNotificationsEnabled}
          onChange={(e)=> setEmailNotificationsEnabled(!!e?.target?.checked)}
        />
        <Form.Group className="mt-2">
          <Form.Label>Notification Recipients (comma-separated)</Form.Label>
          <Form.Control placeholder="owner@example.com, admin@example.com" value={notificationRecipients} onChange={(e)=> setNotificationRecipients(e.target.value)} />
        </Form.Group>
        <Button className="mt-2" variant="outline-primary" size="sm" onClick={async ()=>{
          try {
            // Prevent sending with unsaved edits
            const dirty = (String(notificationRecipients||'').trim() !== String(serverNotificationRecipients||'').trim())
              || (Boolean(emailNotificationsEnabled) !== Boolean(serverEmailNotificationsEnabled));
            if (dirty) {
              alert('You have unsaved notification changes. Please click Save first, then Send Test.');
              return;
            }
            const tok = (await supabase.auth.getSession()).data.session?.access_token || '';
            await fetch(`${edgeBase}/functions/v1/admin-notifications-send-test`, {
              method: 'POST',
              headers: { 'content-type':'application/json', Authorization: `Bearer ${tok}` },
              body: JSON.stringify({ tenant_id: tenantId || '' }),
            });
            alert('Test notification enqueued (if enabled/recipients present).');
          } catch (e) { alert(String(e?.message||e)); }
        }}>Send Test</Button>
      </div>
    </div>
  );
}

function UsageTab() {
  const [preset, setPreset] = useState('last7');
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [series, setSeries] = useState([]);
  const [limits, setLimits] = useState({ pages_limit_effective: null, tokens_per_page_limit_effective: null, chats_per_month_limit_effective: null, sites_limit_effective: null });
  const [chatsUsed, setChatsUsed] = useState(0);
  const [pagesMeters, setPagesMeters] = useState([]);
  const edgeBase = process.env.REACT_APP_SKINNY_SUPABASE_URL;

  const { tenantId, siteId } = useTenant();
  const abortRef = React.useRef(null);

  const load = React.useCallback(async (signal) => {
    debugLog('[UsageTab] load', { tenantId, siteId: siteId || '(empty)', preset });
    setLoading(true);
    try {
      const token = (await supabase.auth.getSession()).data.session?.access_token || '';
      const res = await edgeFetch(`${edgeBase}/functions/v1/user-usage-summary`, {
        method: 'POST',
        headers: { 'content-type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ range: preset, tenant_id: tenantId, site_id: siteId || undefined }),
        signal,
      });
      if (!res.ok) {
        debugLog('[UsageTab] error', { status: res.status, tenantId, siteId });
        setTotal(0); setSeries([]); setLimits({ pages_limit_effective: null, tokens_per_page_limit_effective: null, chats_per_month_limit_effective: null, sites_limit_effective: null }); setChatsUsed(0); setPagesMeters([]); setLoading(false); return;
      }
      const j = await res.json();
      debugLog('[UsageTab] ok', { tenantId, total: j.total });
      setTotal(Number(j.total) || 0);
      setSeries(Array.isArray(j.series) ? j.series : []);
      setLimits(j.limits || { pages_limit_effective: null, tokens_per_page_limit_effective: null, chats_per_month_limit_effective: null, sites_limit_effective: null });
      setChatsUsed(Number(j.chats_used || 0));
      setPagesMeters(Array.isArray(j.pages_meters) ? j.pages_meters : []);
    } catch (err) {
      if (err?.name === 'AbortError') { debugLog('[UsageTab] aborted'); return; }
      debugLog('[UsageTab] exception', { err: err?.message });
      setTotal(0); setSeries([]);
    }
    setLoading(false);
  }, [preset, edgeBase, tenantId, siteId]);

  React.useEffect(() => {
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    load(ctrl.signal);
    return () => ctrl.abort();
  }, [load]);

  const sitesLimit = (typeof limits?.sites_limit_effective === 'number' && limits.sites_limit_effective > 0)
    ? limits.sites_limit_effective
    : null;
  const sitesUsed = pagesMeters.length;
  const siteCapReached = sitesLimit !== null && sitesUsed >= sitesLimit;

  return (
    <div>
      <Row className="g-3 align-items-end mb-3">
        <Col xs="auto">
          <Form.Label className="mb-1">Date Range</Form.Label>
          <Form.Select value={preset} onChange={(e) => setPreset(e.target.value)} aria-label="Date range">
            <option value="today">Today</option>
            <option value="yesterday">Yesterday</option>
            <option value="last7">Last 7 days</option>
            <option value="lastWeek">Last week</option>
            <option value="last30">Last 30 days</option>
            <option value="last90">Last 90 days</option>
          </Form.Select>
        </Col>
        <Col xs="auto" className="ms-auto">
          <div className="text-muted small">Total chats</div>
          <div className="h5 mb-0">{loading ? '…' : total}</div>
        </Col>
      </Row>
      {/* Threshold banners */}
      {(() => {
        const banners = [];
        const chatsLimit = limits?.chats_per_month_limit_effective;
        if (typeof chatsLimit === 'number' && chatsLimit > 0) {
          const ratio = chatsLimit ? (chatsUsed / chatsLimit) : 0;
          if (ratio >= 1) {
            banners.push(
              <div key="chats-100" className="alert alert-danger py-2 mb-3" role="alert">
                Monthly chat limit reached ({chatsUsed} / {chatsLimit}). Resets on 1st (00:00 UTC).
              </div>
            );
          } else if (ratio >= 0.9) {
            banners.push(
              <div key="chats-90" className="alert alert-warning py-2 mb-3" role="alert">
                Approaching monthly chat limit ({Math.round(ratio * 100)}%). {chatsUsed} / {chatsLimit}.
              </div>
            );
          }
        }
        return banners.length ? <div className="mb-3">{banners}</div> : null;
      })()}
      <div className="border rounded p-3 bg-light">
        <UsageChart series={series} title="Usage" />
      </div>
      {/* Meters */}
      <div className="mt-3">
        <div className="fw-semibold mb-1">Current Limits</div>
        <div className="small text-muted">Chats this month</div>
        <div className="mb-2">
          <progress value={limits?.chats_per_month_limit_effective ? Math.min(chatsUsed, limits.chats_per_month_limit_effective) : 0}
                    max={limits?.chats_per_month_limit_effective || Math.max(1, chatsUsed || 1)} />
          <span className="ms-2">{chatsUsed} / {limits?.chats_per_month_limit_effective ?? '∞'}</span>
        </div>
        <div className="small text-muted">Pages per site</div>
        <div className="p-2 border rounded bg-white">
          {pagesMeters.length === 0 ? (
            <div className="text-muted small">No sites.</div>
          ) : (
            pagesMeters.map((m) => (
              <div key={m.site_id} className="mb-2">
                <div className="small">{m.site_url}</div>
                <progress value={m.active_pages} max={m.pages_limit_effective || Math.max(1, m.active_pages)} />
                <span className="ms-2">{m.active_pages} / {m.pages_limit_effective ?? '∞'}</span>
              </div>
            ))
          )}
        </div>
        <div className="mt-3">
          <div className="fw-semibold mb-1">Site Usage</div>
          {sitesLimit ? (
            <>
              <div className="d-flex align-items-center gap-2">
                <progress
                  value={Math.min(sitesUsed, sitesLimit)}
                  max={sitesLimit}
                  style={{ width: '100%' }}
                />
                <span className="small text-nowrap">{sitesUsed} / {sitesLimit}</span>
              </div>
              {siteCapReached ? (
                <div className="text-warning small mt-1">Site cap reached. Upgrade to add more sites.</div>
              ) : null}
            </>
          ) : (
            <div className="text-muted small">Current plan does not limit the number of sites.</div>
          )}
          <div className="small text-muted mt-2">Sites</div>
          {pagesMeters.length === 0 ? (
            <div className="text-muted small">No sites.</div>
          ) : (
            <ul className="list-unstyled small mb-0">
              {pagesMeters.map((m) => (
                <li key={`site-list-${m.site_id || m.site_url}`}>{m.site_url || 'Unnamed site'}</li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function ContentTab() {
  const {
    siteUrl,
    startingUrl,
    criticalInstructions,
    setCriticalInstructions,
    tone,
    setTone,
    primaryFacts,
    setPrimaryFacts,
  } = useSettings();

  const [generatingFacts, setGeneratingFacts] = useState(false);
  const [generateFactsError, setGenerateFactsError] = useState('');

  async function handleGeneratePrimaryFacts() {
    const urlToAnalyze = (startingUrl || siteUrl || '').trim();
    if (!urlToAnalyze) {
      setGenerateFactsError('A Starting URL or Site URL is required to generate facts.');
      return;
    }
    if (primaryFacts && primaryFacts.trim()) {
      if (!window.confirm('This will replace your current Primary Facts. Continue?')) return;
    }
    try {
      setGeneratingFacts(true);
      setGenerateFactsError('');
      const token = (await supabase.auth.getSession()).data.session?.access_token || '';
      const res = await fetch(`${process.env.REACT_APP_SKINNY_SUPABASE_URL}/functions/v1/generate-primary-facts`, {
        method: 'POST',
        headers: { 'content-type': 'application/json', Authorization: `Bearer ${token}`, apikey: process.env.REACT_APP_SKINNY_SUPABASE_ANON_KEY },
        body: JSON.stringify({ siteUrl: urlToAnalyze }),
      });
      if (!res.ok) {
        const err = await res.text();
        console.error('[ContentTab] generate-primary-facts failed:', err);
        setGenerateFactsError('Failed to generate facts. Please try again or enter them manually.');
        return;
      }
      const j = await res.json();
      if (j.primaryFacts) {
        setPrimaryFacts(j.primaryFacts);
      } else {
        setGenerateFactsError('No content was generated. Please try again or enter facts manually.');
      }
    } catch (e) {
      console.error('[ContentTab] generate-primary-facts error:', e);
      setGenerateFactsError('An error occurred. Please try again or enter facts manually.');
    } finally {
      setGeneratingFacts(false);
    }
  }

  const InfoIcon = ({ text }) => (
    <OverlayTrigger placement="right" delay={{ show: 150, hide: 0 }} overlay={<Tooltip id="tip" className="bg-dark">{text}</Tooltip>}>
      <button type="button" className="btn btn-outline-info btn-sm ms-1 p-0 d-inline-flex align-items-center justify-content-center rounded-circle" style={{ width: 18, height: 18, lineHeight: '16px' }} aria-label="Help">
        <i className="bi bi-info-circle" style={{ fontSize: 12 }} aria-hidden="true"></i>
      </button>
    </OverlayTrigger>
  );

  return (
    <Form>
      <Form.Group className="mb-3">
        <Form.Label>
          Critical Instructions
          <InfoIcon text="Non-negotiable guidance the assistant must follow (e.g., Never give medical or legal advice." />
        </Form.Label>
        <Form.Control
          as="textarea"
          rows={5}
          value={criticalInstructions}
          onChange={(e) => setCriticalInstructions(e.target.value)}
          placeholder="Non-negotiable guidance for the assistant (e.g., safety, compliance)."
        />
      </Form.Group>
      <Form.Group className="mb-3">
        <Form.Label>
          Tone
          <InfoIcon text="Desired voice and style (e.g., professional, friendly, concise)." />
        </Form.Label>
        <Form.Control
          as="textarea"
          rows={4}
          value={tone}
          onChange={(e) => setTone(e.target.value)}
          placeholder="Voice and style (e.g., professional, friendly, concise)."
        />
      </Form.Group>
      <Form.Group className="mb-3">
        <Form.Label>
          Primary Facts
          <InfoIcon text="Key facts the assistant can rely on (e.g., product details, pricing ranges, policies)." />
          <Button
            variant="outline-primary"
            size="sm"
            className="ms-2"
            disabled={generatingFacts}
            onClick={handleGeneratePrimaryFacts}
          >
            {generatingFacts ? (
              <>
                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                Generating…
              </>
            ) : (
              <>
                <i className="bi bi-stars me-1" aria-hidden="true"></i>
                AI Generate
              </>
            )}
          </Button>
        </Form.Label>
        {generatingFacts && (
          <div className="text-muted small mb-2">Analyzing your website — this may take 10–15 seconds…</div>
        )}
        {generateFactsError && (
          <Alert variant="warning" className="py-2 mb-2" dismissible onClose={() => setGenerateFactsError('')}>
            {generateFactsError}
          </Alert>
        )}
        <Form.Control
          as="textarea"
          rows={6}
          value={primaryFacts}
          onChange={(e) => setPrimaryFacts(e.target.value)}
          placeholder="Key claims or data points the assistant should rely on."
          disabled={generatingFacts}
        />
      </Form.Group>
    </Form>
  );
}

function TenantSwitcherInline() {
  const { memberships, tenantId, setTenantId } = useTenant();
  return (
    <Form.Group className="text-end">
      <Form.Label className="small text-muted">Tenant</Form.Label>
      <Form.Select size="sm" value={tenantId} onChange={(e)=> { debugLog('[TenantSwitcher] switch', { from: tenantId, to: e.target.value }); setTenantId(e.target.value); }} disabled={(memberships||[]).length<=1}>
        {(memberships||[]).map((m)=> (
          <option key={m.tenant_id} value={m.tenant_id}>{m.tenant_name || m.tenant_id} ({m.role})</option>
        ))}
      </Form.Select>
    </Form.Group>
  );
}

function SiteSwitcherInline() {
  const { tenantId, siteId, setSiteId, siteRefreshToken } = useTenant();
  const { tenantSites, settingsReady } = useSettings();
  const sites = React.useMemo(() => tenantSites || [], [tenantSites]);

  React.useEffect(() => {
    if (!tenantId || sites.length === 0) return;
    if (!settingsReady) {
      debugLog('[SiteSwitcher] skipping: settings not ready', { tenantId, siteId, sitesCount: sites.length });
      return;
    }
    const siteIds = new Set(sites.map(s => s.id));
    const key = `site_sel_${tenantId}`;
    const persisted = (typeof window !== 'undefined') ? (localStorage.getItem(key) || '') : '';
    const current = (siteId && siteIds.has(siteId)) ? siteId
      : (persisted && siteIds.has(persisted)) ? persisted
      : '';
    const next = current || (sites[0]?.id || '');
    if (next && next !== siteId) {
      debugLog('[SiteSwitcher] setSiteId', { tenantId, from: siteId, to: next });
      try { localStorage.setItem(key, next); } catch (_) {}
      setSiteId(next);
    }
  }, [tenantId, siteId, setSiteId, sites, siteRefreshToken, settingsReady]);

  function onChange(e) {
    const v = e.target.value;
    setSiteId(v);
    if (tenantId && typeof window !== 'undefined') {
      try { localStorage.setItem(`site_sel_${tenantId}`, v || ''); } catch (_) {}
      try {
        if (typeof window.__skinny_setContext === 'function') {
          window.__skinny_setContext({ tenantId, siteId: v || '' });
        }
      } catch (_) {}
    }
  }
  return (
    <Form.Group className="text-end">
      <Form.Label className="small text-muted">Site</Form.Label>
      <Form.Select size="sm" value={siteId || ''} onChange={onChange} disabled={sites.length<=1}>
        {sites.map((s)=> (
          <option key={s.id} value={s.id}>{s.site_url || s.id}</option>
        ))}
      </Form.Select>
    </Form.Group>
  );
}

function SitesTab({ planRefreshTick = 0 }) {
  const { tenantId, memberships, siteId, setSiteId, notifySiteChange } = useTenant();
  const [sites, setSites] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [confirm, setConfirm] = React.useState(null); // { id, site_url }
  const [confirmText, setConfirmText] = React.useState('');
  const [busyDelete, setBusyDelete] = React.useState(false);
  const [allowMultiSite, setAllowMultiSite] = React.useState(false);
  const [sitesLimit, setSitesLimit] = React.useState(null);
  const [showAdd, setShowAdd] = React.useState(false);
  const edgeBase = process.env.REACT_APP_SKINNY_SUPABASE_URL;

  const myRole = React.useMemo(() => {
    try { return String((memberships||[]).find(m=>m.tenant_id===tenantId)?.role || '').toLowerCase(); } catch (_) { return ''; }
  }, [memberships, tenantId]);
  const canDelete = myRole === 'owner' || myRole === 'admin' || myRole === 'superadmin';

  const loadSitesAndPlan = React.useCallback(async () => {
    if (!tenantId) { setSites([]); setAllowMultiSite(false); return []; }
    setLoading(true); setError('');
    try {
      const { data } = await import('../lib/supabaseClient');
      const supabase = data?.supabase || (await import('../lib/supabaseClient')).supabase;
      const tok = (await supabase.auth.getSession()).data.session?.access_token || '';
      const res = await fetch(`${edgeBase}/functions/v1/settings-get?tenant_id=${encodeURIComponent(tenantId)}`, { headers: { Authorization: `Bearer ${tok}` } });
      if (!res.ok) {
        setError(`Load failed: ${res.status}`);
        setSites([]);
        setAllowMultiSite(false);
        setLoading(false);
        return;
      }
      const j = await res.json();
      const arr = Array.isArray(j?.sites) ? j.sites : [];
      setSites(arr);
      const limit = Number((j?.tenant_limits?.sites_limit_effective ?? null));
      setSitesLimit(Number.isFinite(limit) ? limit : null);
      setAllowMultiSite(Number.isFinite(limit) ? (limit >= 2) : false);
      setLoading(false);
      return arr;
    } catch (e) {
      setError(String(e?.message || e));
      setSites([]);
      setAllowMultiSite(false);
      setLoading(false);
      return [];
    }
  }, [tenantId, edgeBase]);

  React.useEffect(() => { loadSitesAndPlan(); }, [loadSitesAndPlan, planRefreshTick]);

  async function handleDelete(site) {
    if (!tenantId || !site?.id) return;
    setBusyDelete(true); setError('');
    try {
      const { data } = await import('../lib/supabaseClient');
      const supabase = data?.supabase || (await import('../lib/supabaseClient')).supabase;
      const tok = (await supabase.auth.getSession()).data.session?.access_token || '';
      const res = await fetch(`${edgeBase}/functions/v1/user-sites-delete`, {
        method: 'POST',
        headers: { 'content-type': 'application/json', Authorization: `Bearer ${tok}` },
        body: JSON.stringify({ tenant_id: tenantId, site_id: site.id }),
      });
      const txt = await res.text();
      if (!res.ok) { setError(txt || `Delete failed: ${res.status}`); setBusyDelete(false); return; }
      // If deleted site was selected, clear and choose fallback after reload
      try {
        const key = `site_sel_${tenantId}`;
        const selected = (typeof window !== 'undefined') ? (localStorage.getItem(key) || '') : '';
        if (selected && selected === site.id) {
          localStorage.removeItem(key);
        }
      } catch (_) {}
      setConfirm(null);
      const remaining = await loadSitesAndPlan();
      notifySiteChange();
      if (siteId && siteId === site.id) {
        const fallback = remaining.find((s) => s.id && s.id !== site.id)?.id || remaining[0]?.id || '';
        if (fallback && fallback !== siteId) {
          setSiteId(fallback);
        } else if (!fallback) {
          setSiteId('');
        }
      }
    } catch (e) {
      setError(String(e?.message || e));
    }
    setBusyDelete(false);
  }

  if (!tenantId) {
    return <div className="alert alert-warning">Select a tenant to manage sites.</div>;
  }

  if (!allowMultiSite) {
    return (
      <div className="p-3">
        <div className="alert alert-info mb-3">
          Multi‑site management is not enabled for this tenant. Upgrade your plan or contact support to increase the allowed site count.
        </div>
      </div>
    );
  }

  return (
    <div className="p-3">
      {error ? <div className="alert alert-danger">{error}</div> : null}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="fw-semibold">Sites</div>
        <div className="d-flex gap-2">
          <Button variant="secondary" onClick={loadSitesAndPlan} disabled={loading}>Refresh</Button>
          <Button
            variant="primary"
            disabled={!allowMultiSite || !canDelete || (Number.isFinite(sitesLimit) && sitesLimit !== null && sites.length >= Number(sitesLimit))}
            onClick={() => setShowAdd(true)}
            title={!canDelete ? 'Only owners/admins/superadmins can add sites' : undefined}
          >
            + Add Site
          </Button>
        </div>
      </div>
      {loading ? (
        <div className="text-muted">Loading…</div>
      ) : (
        <div className="table-responsive">
          <Table hover size="sm" className="align-middle">
            <thead>
              <tr>
                <th>Site URL</th>
                <th>Starting URL</th>
                <th>Status</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {(sites||[]).length ? (sites||[]).map((s) => (
                <tr key={s.id}>
                  <td>{s.site_url || s.id}</td>
                  <td className="text-truncate" style={{maxWidth:'320px'}}>{s.starting_url || '—'}</td>
                  <td className="text-capitalize">{s.status || '—'}</td>
                  <td className="text-end">
                    <Button
                      variant="outline-danger"
                      size="sm"
                      disabled={!canDelete || (sites || []).length <= 1}
                      onClick={() => { setConfirm({ id: s.id, site_url: s.site_url || s.id }); setConfirmText(''); }}
                      title={
                        !canDelete
                          ? 'Only owners/admins can delete sites'
                          : (sites || []).length <= 1
                            ? 'Cannot delete the only remaining site'
                            : 'Delete site'
                      }
                    >
                      Delete
                    </Button>
                  </td>
                </tr>
              )) : (
                <tr><td colSpan={4}><div className="text-muted">No sites found.</div></td></tr>
              )}
            </tbody>
          </Table>
        </div>
      )}
      <Modal show={!!confirm} onHide={() => { setConfirm(null); setConfirmText(''); }} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete Site</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-2">
            This will permanently delete <strong>{confirm?.site_url || confirm?.id}</strong> and all associated data (themes, auto‑launch, crawler artifacts, analytics, and more).
          </div>
          <div className="text-danger small">This action cannot be undone.</div>
          <hr />
          <div className="mb-2">
            To confirm, type the site URL exactly:
          </div>
          <Form.Control
            type="text"
            placeholder={confirm?.site_url || 'https://example.com'}
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { setConfirm(null); setConfirmText(''); }}>Cancel</Button>
          <Button
            variant="danger"
            disabled={
              busyDelete ||
              !confirm ||
              String(confirmText || '').trim() !== String(confirm?.site_url || '')
            }
            onClick={() => handleDelete(confirm)}
            title="Enter the exact site URL to enable delete"
          >
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
      {showAdd ? (
        <AddSiteModal
          show={showAdd}
          onHide={()=>setShowAdd(false)}
          tenantId={tenantId}
          existingSiteUrls={(sites || []).map(s => s.site_url || '')}
          limits={{ sitesLimit: (sitesLimit ?? null) }}
          onCreated={async (newId) => {
            setShowAdd(false);
            if (newId && tenantId && typeof window !== 'undefined') {
              try { localStorage.setItem(`site_sel_${tenantId}`, newId); } catch (_) {}
            }
            await loadSitesAndPlan();
            notifySiteChange();
            if (newId) setSiteId(newId);
          }}
        />
      ) : null}
    </div>
  );
}

function TeamTab({ planId = '', isProBono = false }) {
  const { tenantId, memberships } = useTenant();
  const { tenantLimits: ctxLimits } = useSettings();
  const [members, setMembers] = React.useState([]);
  const [pending, setPending] = React.useState([]);
  const [seatCapOverride, setSeatCapOverride] = React.useState(undefined);
  const [email, setEmail] = React.useState('');
  const [role, setRole] = React.useState('editor');
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [currentUserId, setCurrentUserId] = React.useState('');
  const [removeTarget, setRemoveTarget] = React.useState(null);
  const [removeBusy, setRemoveBusy] = React.useState(false);
  const [removeError, setRemoveError] = React.useState('');
  const [toastMsg, setToastMsg] = React.useState('');
  const edgeBase = process.env.REACT_APP_SKINNY_SUPABASE_URL;

  React.useEffect(() => {
    if (ctxLimits) {
      const rawSeatCap = ctxLimits.seats_limit_effective;
      if (rawSeatCap === null) {
        setSeatCapOverride(null);
      } else {
        const parsedSeatCap = Number(rawSeatCap);
        setSeatCapOverride(Number.isFinite(parsedSeatCap) ? parsedSeatCap : undefined);
      }
    } else {
      setSeatCapOverride(undefined);
    }
  }, [ctxLimits]);

  const load = React.useCallback(async () => {
    if (!tenantId) return;
    debugLog('[Dashboard:TeamTab] load members', { tenantId });
    setLoading(true); setError('');
    try {
      const tok = (await supabase.auth.getSession()).data.session?.access_token || '';
      const headers = { 'content-type': 'application/json', Authorization: `Bearer ${tok}` };
      const res = await fetch(`${edgeBase}/functions/v1/team-list`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ tenant_id: tenantId })
      });
      if (!res.ok) { setMembers([]); setPending([]); setError(`Load failed: ${res.status}`); setLoading(false); return; }
      const j = await res.json();
      const mem = Array.isArray(j.members) ? j.members : [];
      const inv = Array.isArray(j.pending_invites) ? j.pending_invites : [];
      setMembers(mem);
      setPending(inv);
    } catch (e) { setError(String(e?.message||e)); setMembers([]); setPending([]); }
    setLoading(false);
  }, [tenantId, edgeBase]);

  React.useEffect(() => { load(); }, [load]);
  React.useEffect(() => { (async () => { try { const u = (await supabase.auth.getUser()).data.user; setCurrentUserId(u?.id || ''); } catch (_) {} })(); }, []);

  const fallbackSeatCap = planId === 'team'
    ? 5
    : planId === 'enterprise'
      ? 10
      : (isProBono ? 5 : 1);
  const seatCap = seatCapOverride === undefined ? fallbackSeatCap : seatCapOverride;
  const inviteDisabledReason = React.useMemo(() => {
    if (isProBono) return '';
    if (planId === 'trial') return 'Invites are disabled on the Trial plan. Upgrade to enable team access.';
    if (planId === 'pro') return 'The Pro plan is single-seat. Upgrade to Team or Enterprise to invite others.';
    return '';
  }, [planId, isProBono]);

  async function invite() {
    if (!tenantId || !email.trim()) return;
    // Seat cap precheck (Active members + Pending invites)
    try {
      const activeCount = (members||[]).filter(m => String(m.status||'').toLowerCase() === 'active').length;
      const pendingCount = (pending||[]).length;
      if (seatCap !== null && activeCount + pendingCount >= seatCap) {
        setError(`Seat limit reached (${seatCap}). Revoke an invite or remove a member to free a seat.`);
        return;
      }
    } catch (_) {}
    setLoading(true); setError('');
    try {
      const tok = (await supabase.auth.getSession()).data.session?.access_token || '';
      const res = await fetch(`${edgeBase}/functions/v1/team-invite`, { method: 'POST', headers: { 'content-type': 'application/json', Authorization: `Bearer ${tok}` }, body: JSON.stringify({ tenant_id: tenantId, email: email.trim(), role, app_origin: window.location.origin }) });
      const txt = await res.text();
      if (!res.ok) { setError(txt || 'Invite failed'); setLoading(false); return; }
      try {
        const j = JSON.parse(txt);
        if (j?.accept_url) {
          try { await navigator.clipboard.writeText(j.accept_url); setError(''); setToastMsg(j?.email_sent ? 'Invite email sent. Link copied to clipboard.' : 'Invite created. Link copied to clipboard.'); } catch (_) { setToastMsg(j?.email_sent ? 'Invite email sent.' : 'Invite created.'); }
        }
      } catch (_) {}
      setEmail(''); await load();
    } catch (e) { setError(String(e?.message||e)); }
    setLoading(false);
  }

  const myRole = React.useMemo(() => {
    try { return String((memberships||[]).find(m=>m.tenant_id===tenantId)?.role || '').toLowerCase(); } catch (_) { return ''; }
  }, [memberships, tenantId]);

  const ownersCount = React.useMemo(() => {
    try { return (members||[]).filter(m => String(m.role||'').toLowerCase() === 'owner').length; } catch (_) { return 0; }
  }, [members]);
  const activeCount = React.useMemo(() => {
    try { return (members||[]).filter(m => String(m.status||'').toLowerCase() === 'active').length; } catch (_) { return 0; }
  }, [members]);
  const pendingCount = React.useMemo(() => (pending||[]).length, [pending]);
  const seatsUsed = activeCount + pendingCount;
  const atSeatCap = seatCap !== null && seatsUsed >= seatCap;

  async function updateRole(targetAuthUserId, newRole) {
    if (!tenantId || !targetAuthUserId || !newRole) return;
    setLoading(true); setError('');
    try {
      const tok = (await supabase.auth.getSession()).data.session?.access_token || '';
      const res = await fetch(`${edgeBase}/functions/v1/team-update-role`, {
        method: 'POST', headers: { 'content-type': 'application/json', Authorization: `Bearer ${tok}` },
        body: JSON.stringify({ tenant_id: tenantId, target_auth_user_id: targetAuthUserId, new_role: newRole })
      });
      const txt = await res.text();
      if (!res.ok) { setError(txt || 'Role update failed'); setLoading(false); return; }
      setToastMsg('Role updated.');
      await load();
    } catch (e) { setError(String(e?.message||e)); }
    setLoading(false);
  }

  function askRemove(m) { setRemoveTarget(m); setRemoveError(''); }
  function closeRemove() { if (removeBusy) return; setRemoveTarget(null); setRemoveError(''); }
  async function confirmRemove() {
    if (!removeTarget || !tenantId) return;
    setRemoveBusy(true); setRemoveError('');
    try {
      const tok = (await supabase.auth.getSession()).data.session?.access_token || '';
      const res = await fetch(`${edgeBase}/functions/v1/team-remove-member`, {
        method: 'POST', headers: { 'content-type': 'application/json', Authorization: `Bearer ${tok}` },
        body: JSON.stringify({ tenant_id: tenantId, target_auth_user_id: removeTarget.auth_user_id })
      });
      const txt = await res.text();
      if (!res.ok) { setRemoveError(txt || `Remove failed: ${res.status}`); setRemoveBusy(false); return; }
      setRemoveBusy(false);
      setRemoveTarget(null);
      load();
    } catch (e) {
      setRemoveError(String(e?.message || e));
      setRemoveBusy(false);
    }
  }

  return (
    <div>
      <div className="d-flex align-items-end gap-2 mb-2 flex-wrap">
        <Form.Group>
          <Form.Label>Invite Email</Form.Label>
          <Form.Control type="email" value={email} onChange={(e)=>setEmail(e.target.value)} placeholder="user@example.com" />
        </Form.Group>
        <Form.Group>
          <Form.Label>Role</Form.Label>
          <Form.Select value={role} onChange={(e)=>setRole(e.target.value)}>
            <option value="editor">Editor</option>
            <option value="admin">Admin</option>
            <option value="owner">Owner</option>
          </Form.Select>
        </Form.Group>
        <Button
          className="mb-3"
          onClick={invite}
          disabled={loading || !tenantId || !email.trim() || atSeatCap || !!inviteDisabledReason}
          title={inviteDisabledReason || undefined}
        >
          Invite
        </Button>
        <div className="ms-auto mb-1 small text-muted">Seats used {seatsUsed}/{seatCap === null ? '∞' : seatCap} (Active + Pending)</div>
      </div>
      {inviteDisabledReason ? (
        <div className="alert alert-info py-2">{inviteDisabledReason}</div>
      ) : null}
      {error ? <div className="alert alert-warning py-2">{error}</div> : null}
      {loading ? <div className="text-muted">Loading…</div> : (
        <Row className="g-3">
          <Col md>
            <div className="border rounded p-3 bg-light mb-3">
              <div className="fw-semibold mb-2">Members</div>
              <Table size="sm" responsive>
                <thead><tr><th>Email</th><th>Role</th><th>Joined</th><th>Actions</th></tr></thead>
                <tbody>
                  {members.map((m,i)=>{
                    const isSelf = currentUserId && m.auth_user_id === currentUserId;
                    const isOwner = String(m.role||'').toLowerCase()==='owner';
                    const my = String(myRole||'').toLowerCase();
                    const canRemoveOwner = (my === 'owner' || my === 'superadmin');
                    const canRemove = !isSelf && (!isOwner || canRemoveOwner);
                    const canChangeRole = (String(myRole||'')==='owner' || String(myRole||'')==='superadmin') && !isSelf;
                    const disableOwnerDemote = canChangeRole && isOwner && ownersCount <= 1;
                    return (
                      <tr key={i}>
                        <td>{m.email}</td>
                        <td>
                          {canChangeRole ? (
                            <Form.Select size="sm" value={String(m.role||'').toLowerCase()} onChange={(e)=> updateRole(m.auth_user_id, e.target.value)} disabled={disableOwnerDemote}>
                              <option value="owner">Owner</option>
                              <option value="admin">Admin</option>
                              <option value="editor">Editor</option>
                            </Form.Select>
                          ) : (
                            <span className="text-capitalize">{m.role}</span>
                          )}
                        </td>
                        <td>{m.created_at?new Date(m.created_at).toLocaleString():'—'}</td>
                        <td>
                          <Button size="sm" variant="outline-danger" disabled={!canRemove} onClick={()=> askRemove(m)}>Remove</Button>
                        </td>
                      </tr>
                    );
                  })}
                  {members.length===0 && (<tr><td colSpan={4} className="text-muted small">No members</td></tr>)}
                </tbody>
              </Table>
            </div>
          </Col>
          <Col md>
            <div className="border rounded p-3 bg-light mb-3">
              <div className="fw-semibold mb-2">Pending Invites</div>
              <div className="text-muted small">Invited members appear after they accept.</div>
              <Table size="sm" responsive>
                <thead><tr><th>Email</th><th>Role</th><th>Status</th><th>Actions</th></tr></thead>
                <tbody>
                  {pending.map((m,i)=>(
                    <tr key={i}>
                      <td>{m.email}</td>
                      <td className="text-capitalize">{m.role || '—'}</td>
                      <td>{m.status}</td>
                      <td className="d-flex gap-2">
                        <Button size="sm" variant="outline-secondary" onClick={async ()=>{
                          try {
                            const tok = (await supabase.auth.getSession()).data.session?.access_token || '';
                            const res = await fetch(`${edgeBase}/functions/v1/team-invite`, { method: 'POST', headers: { 'content-type': 'application/json', Authorization: `Bearer ${tok}` }, body: JSON.stringify({ tenant_id: tenantId, email: m.email, role: (m.role||'editor'), app_origin: window.location.origin }) });
                            const txt = await res.text();
                            if (res.ok) { try { const j = JSON.parse(txt); if (j?.accept_url) { try { await navigator.clipboard.writeText(j.accept_url); setToastMsg(j?.email_sent ? 'Invite email re-sent. Link copied.' : 'Invite re-created. Link copied.'); } catch (_) { setToastMsg(j?.email_sent ? 'Invite email re-sent.' : 'Invite re-created.'); } } } catch (_) {} load(); }
                          } catch (_) {}
                        }}>Re-send</Button>
                        <Button size="sm" variant="outline-danger" onClick={async ()=>{
                          try {
                            const tok = (await supabase.auth.getSession()).data.session?.access_token || '';
                            const res = await fetch(`${edgeBase}/functions/v1/team-revoke-invite`, { method: 'POST', headers: { 'content-type': 'application/json', Authorization: `Bearer ${tok}` }, body: JSON.stringify({ tenant_id: tenantId, invitation_id: m.id }) });
                            if (res.ok) { setToastMsg('Invite revoked.'); load(); }
                          } catch (_) {}
                        }}>Revoke</Button>
                      </td>
                    </tr>
                  ))}
                  {pending.length===0 && (<tr><td colSpan={4} className="text-muted small">No pending invites</td></tr>)}
                </tbody>
              </Table>
            </div>
          </Col>
        </Row>
      )}
      <Modal show={!!removeTarget} onHide={closeRemove} centered>
        <Modal.Header closeButton>
          <Modal.Title>Remove Member</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {removeError ? (<div className="alert alert-warning py-2 mb-2">{removeError}</div>) : null}
          {removeTarget ? (
            <div>
              Are you sure you want to remove <strong>{removeTarget.email}</strong> from this tenant?
            </div>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeRemove} disabled={removeBusy}>Cancel</Button>
          <Button variant="danger" onClick={confirmRemove} disabled={removeBusy}>{removeBusy ? 'Removing…' : 'Remove'}</Button>
        </Modal.Footer>
      </Modal>
      <ToastContainer position="bottom-center" className="p-3">
        <Toast onClose={() => setToastMsg('')} show={!!toastMsg} delay={3000} autohide bg="success">
          <Toast.Body className="text-white">{toastMsg}</Toast.Body>
        </Toast>
      </ToastContainer>
    </div>
  );
}
function HistoryTab() {
  const [preset, setPreset] = useState('last7');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [includeTranscripts, setIncludeTranscripts] = useState(false);
  const [contactSession, setContactSession] = useState(null);
  const [contactText, setContactText] = useState('');
  const edgeBase = process.env.REACT_APP_SKINNY_SUPABASE_URL;

  function emojiFromSentiment(s) {
    const x = String(s || '').toUpperCase();
    if (x.includes('HAPPY') || x.includes('POSITIVE')) return '😀';
    if (x.includes('NEUTRAL')) return '😐';
    if (x.includes('NEGATIVE') || x.includes('SAD')) return '☹️';
    if (x.includes('ANGRY')) return '😠';
    return s ? '🤔' : '—';
  }

  const { tenantId, siteId } = useTenant();
  const abortRef = React.useRef(null);

  const load = React.useCallback(async (signal) => {
    if (!tenantId || !tenantId.trim()) {
      debugLog('[HistoryTab] skip: no tenantId');
      setRows([]);
      setLoading(false);
      return;
    }
    debugLog('[HistoryTab] load', { tenantId, siteId: siteId || '(empty)', preset });
    setLoading(true);
    try {
      const token = (await supabase.auth.getSession()).data.session?.access_token || '';
      if (!token) {
        setRows([]);
        setLoading(false);
        return;
      }
      const res = await edgeFetch(`${edgeBase}/functions/v1/user-chat-sessions`, {
        method: 'POST', headers: { 'content-type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ range: preset, tenant_id: tenantId.trim(), site_id: siteId || undefined }),
        signal,
      });
      if (!res.ok) {
        const errorText = await res.text().catch(() => '');
        debugLog('[HistoryTab] error', { status: res.status, tenantId, siteId, errorText });
        setRows([]);
        setLoading(false);
        return;
      }
      const j = await res.json();
      debugLog('[HistoryTab] ok', { tenantId, sessions: (j.sessions || []).length });
      setRows(Array.isArray(j.sessions) ? j.sessions : []);
    } catch (err) {
      if (err?.name === 'AbortError') { debugLog('[HistoryTab] aborted'); return; }
      debugLog('[HistoryTab] exception', { err: err?.message });
      setRows([]);
    }
    setLoading(false);
  }, [preset, edgeBase, tenantId, siteId]);

  React.useEffect(() => {
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    load(ctrl.signal);
    return () => ctrl.abort();
  }, [load]);

  // Download helpers (PDF/RTF with optional transcripts)
  function escapeRtf(text) {
    return String(text || '')
      .replace(/\\/g, '\\\\')
      .replace(/[{}]/g, (m) => '\\' + m)
      .replace(/\n/g, '\\line ');
  }
  function downloadBlob(filename, mime, content) {
    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; document.body.appendChild(a); a.click();
    setTimeout(()=>{ URL.revokeObjectURL(url); document.body.removeChild(a); }, 0);
  }
  function buildFileBase() {
    const d = new Date();
    const ymd = `${d.getUTCFullYear()}${String(d.getUTCMonth()+1).padStart(2,'0')}${String(d.getUTCDate()).padStart(2,'0')}`;
    return `history_mytenant_${ymd}_${preset}`;
  }
  async function fetchTranscriptsMap(ids, selectedSiteId) {
    const out = {};
    try {
      const token = (await supabase.auth.getSession()).data.session?.access_token || '';
      for (const id of ids) {
        try {
          const res = await fetch(`${edgeBase}/functions/v1/admin-chat-messages`, {
            method: 'POST', headers: { 'content-type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ session_id: id, tenant_id: tenantId || undefined, site_id: selectedSiteId || undefined })
          });
          if (!res.ok) { out[id] = []; continue; }
          const j = await res.json();
          out[id] = Array.isArray(j.messages) ? j.messages : [];
        } catch (_) { out[id] = []; }
      }
    } catch (_) { /* ignore */ }
    return out;
  }
  // Message text/role helpers (mirror Chat Details dialog extraction)
  function getPlainTextFromMsg(m) {
    try {
      if (!m) return '';
      if (m.response && typeof m.response.text === 'string') return m.response.text;
      if (m.text && typeof m.text === 'string') return m.text;
      if (typeof m.content === 'string') {
        const raw = m.content.trim();
        if ((raw.startsWith('{') && raw.endsWith('}')) || raw.startsWith('"{"')) {
          try {
            const obj = JSON.parse(raw);
            if (obj && obj.response && typeof obj.response.text === 'string') return obj.response.text;
            if (obj && typeof obj.text === 'string') return obj.text;
          } catch (_) {
            try {
              const obj2 = JSON.parse(raw.replace(/^"|"$/g, ''));
              if (obj2 && obj2.response && typeof obj2.response.text === 'string') return obj2.response.text;
              if (obj2 && typeof obj2.text === 'string') return obj2.text;
            } catch (_) { /* ignore */ }
          }
        }
        return raw;
      }
      return '';
    } catch (_) { return ''; }
  }
  function getRoleLabel(m) {
    try { return String(m.role || '').toLowerCase() === 'assistant' ? 'SkinnyAI' : 'User'; } catch (_) { return 'User'; }
  }
  function buildRtfFromHistory(rows, transcriptsById, withTranscripts) {
    const parts = [];
    parts.push('{\\rtf1\\ansi\\deff0');
    const header = `TheSkinnyAI History Report\\line Range: ${escapeRtf(preset)}\\line \\line`;
    parts.push(header);
    parts.push('\\b Sessions \\b0\\line ');
    rows.forEach((r) => {
      const last = r.last_activity ? new Date(r.last_activity).toLocaleString() : '—';
      const lead = (typeof r.last_lead_confidence === 'number') ? Math.round(Number(r.last_lead_confidence)*100)+'%' : '—';
      parts.push(`Time: ${escapeRtf(last)}\\line Sentiment: ${escapeRtf(String(r.last_sentiment||'').toUpperCase())}\\line Lead: ${escapeRtf(lead)}\\line Request ID: ${escapeRtf(r.last_request_id||'—')}\\line Messages: ${escapeRtf(String(r.message_count||'—'))}\\line `);
      if (withTranscripts) {
        const msgs = transcriptsById?.[r.id] || [];
        if (msgs.length) {
          parts.push('\\b Transcript \\b0\\line ');
          msgs.forEach((m) => {
            const ts = m.ts ? new Date(m.ts).toLocaleString() : '';
            const role = getRoleLabel(m);
            const text = getPlainTextFromMsg(m);
            const line = `[${ts}] ${role}: ${text}`;
            parts.push(escapeRtf(line) + '\\line ');
          });
        }
        parts.push('\\line ');
      }
    });
    parts.push('}');
    return parts.join('');
  }
  function openPrintHistory(rows, transcriptsById, withTranscripts) {
    const w = window.open('', '_blank'); if (!w) return;
    const html = `<!doctype html><html><head><meta charset="utf-8"/><title>TheSkinnyAI History Report</title>
    <style>body{font-family:system-ui,-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;padding:24px;} h1{font-size:20px;margin:0 0 8px;} h2{font-size:16px;margin:16px 0 8px;} .muted{color:#666;font-size:12px} table{border-collapse:collapse;width:100%;} td,th{border:1px solid #ddd;padding:6px;font-size:12px} .mono{font-family:ui-monospace, SFMono-Regular, Menlo, monospace}</style></head><body>
    <h1>TheSkinnyAI History Report</h1>
    <div class="muted">Range: ${preset}</div>
    <h2>Sessions</h2>
    <table><thead><tr><th>Last Activity</th><th>Sentiment</th><th>Lead</th><th>Request ID</th><th>Messages</th></tr></thead><tbody>
    ${rows.map(r=>{
      const last = r.last_activity ? new Date(r.last_activity).toLocaleString() : '—';
      const lead = (typeof r.last_lead_confidence === 'number') ? Math.round(Number(r.last_lead_confidence)*100)+'%' : '—';
      return `<tr><td>${last}</td><td>${r.last_sentiment||'—'}</td><td>${lead}</td><td class="mono">${r.last_request_id||'—'}</td><td>${typeof r.message_count==='number'?r.message_count:'—'}</td></tr>`;
    }).join('')}
    </tbody></table>
    ${withTranscripts ? rows.map(r=>{
      const msgs = transcriptsById?.[r.id]||[];
      if (!msgs.length) return '';
      const last = r.last_activity ? new Date(r.last_activity).toLocaleString() : '';
      return `<h2>Transcript · ${last}</h2>` + msgs.map(m=>{
        const ts = m.ts ? new Date(m.ts).toLocaleString() : '';
        const role = (String(m.role||'').toLowerCase()==='assistant') ? 'SkinnyAI' : 'User';
        let txt = '';
        try {
          if (m.response && typeof m.response.text === 'string') txt = m.response.text;
          else if (m.text && typeof m.text === 'string') txt = m.text;
          else if (typeof m.content === 'string') {
            const raw = m.content.trim();
            if ((raw.startsWith('{') && raw.endsWith('}')) || raw.startsWith('"{"')) {
              try {
                const obj = JSON.parse(raw);
                if (obj && obj.response && typeof obj.response.text === 'string') txt = obj.response.text;
                else if (obj && typeof obj.text === 'string') txt = obj.text;
                else txt = raw;
              } catch (_) {
                try {
                  const obj2 = JSON.parse(raw.replace(/^"|"$/g, ''));
                  if (obj2 && obj2.response && typeof obj2.response.text === 'string') txt = obj2.response.text;
                  else if (obj2 && typeof obj2.text === 'string') txt = obj2.text;
                  else txt = raw;
                } catch (_) { txt = raw; }
              }
            } else txt = raw;
          }
        } catch (_) { txt = ''; }
        return `<div class="mono">[${ts}] ${role}: ${txt}</div>`;
      }).join('');
    }).join('') : ''}
    <script>window.onload=()=>{window.print()};</script>
    </body></html>`;
    w.document.open(); w.document.write(html); w.document.close();
  }
  async function handleDownload(format) {
    const ids = (rows||[]).map(r=>r.id).filter(Boolean);
    const selectedSiteId = siteId || ((typeof window !== 'undefined' && tenantId)
      ? (localStorage.getItem(`site_sel_${tenantId}`) || '')
      : '');
    const transcriptsById = includeTranscripts && ids.length ? await fetchTranscriptsMap(ids, selectedSiteId || undefined) : {};
    if (format === 'rtf') {
      const rtf = buildRtfFromHistory(rows, transcriptsById, includeTranscripts);
      const base = buildFileBase();
      downloadBlob(base + '.rtf', 'application/rtf', rtf);
    } else if (format === 'pdf') {
      openPrintHistory(rows, transcriptsById, includeTranscripts);
    }
  }

  return (
    <div>
      <Row className="g-3 align-items-end mb-3">
        <Col xs="auto">
          <Form.Label className="mb-1">Date Range</Form.Label>
          <Form.Select value={preset} onChange={(e) => setPreset(e.target.value)} aria-label="Date range">
            <option value="today">Today</option>
            <option value="yesterday">Yesterday</option>
            <option value="last7">Last 7 days</option>
            <option value="lastWeek">Last week</option>
            <option value="last30">Last 30 days</option>
            <option value="last90">Last 90 days</option>
          </Form.Select>
        </Col>
        <Col xs="auto" className="pt-4 d-flex align-items-center gap-2">
          <Form.Check type="checkbox" id="user-hist-inc-transcripts" label="Include Transcripts" checked={includeTranscripts} onChange={(e)=>setIncludeTranscripts(e.target.checked)} />
        </Col>
        <Col xs="auto" className="pt-4">
          <Dropdown as={ButtonGroup}>
            <Button variant="outline-secondary" disabled={!rows.length} onClick={()=>handleDownload('pdf')}>Download</Button>
            <Dropdown.Toggle split variant="outline-secondary" id="user-hist-download" disabled={!rows.length} />
            <Dropdown.Menu>
              <Dropdown.Item disabled={!rows.length} onClick={()=>handleDownload('pdf')}>Print (PDF)</Dropdown.Item>
              <Dropdown.Item disabled={!rows.length} onClick={()=>handleDownload('rtf')}>RTF</Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </Col>
      </Row>
      {loading ? (
        <div className="text-muted">Loading…</div>
      ) : (
        <Table hover responsive>
          <thead>
            <tr>
              <th>Last Activity</th>
              <th>Sentiment</th>
              <th>Lead Confidence</th>
              <th>Request ID</th>
              <th>Messages</th>
              <th>Contact</th>
              <th>Details</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, idx) => (
              <tr key={idx}>
                <td>{r.last_activity ? new Date(r.last_activity).toLocaleString() : '—'}</td>
                <td>{emojiFromSentiment(r.last_sentiment)}</td>
                <td>{typeof r.last_lead_confidence === 'number' ? Math.round(Number(r.last_lead_confidence)*100) + '%' : '—'}</td>
                <td className="text-monospace small">{r.last_request_id || '—'}</td>
                <td>{typeof r.message_count === 'number' ? r.message_count : '—'}</td>
                <td>
                  {(() => {
                    const cnt = Number(r.last_contact_count || 0);
                    const variant = cnt >= 3 ? 'success' : (cnt > 0 ? 'warning' : 'light');
                    const tip = `Contact: ${cnt} of 3`;
                    return (
                      <OverlayTrigger placement="top" overlay={<Tooltip id={`tip-${r.id}`}>{tip}</Tooltip>}>
                        <Button
                          size="sm"
                          variant={variant}
                          onClick={async () => {
                            try {
                              setContactSession(r);
                              const seedLines = [];
                              if (r.last_contact_name) seedLines.push(`Name: ${r.last_contact_name}`);
                              if (r.last_contact_email) seedLines.push(`Email: ${r.last_contact_email}`);
                              if (r.last_contact_phone) seedLines.push(`Phone: ${r.last_contact_phone}`);
                              setContactText(seedLines.length ? seedLines.join('\n') : 'Loading…');
                              const token = (await supabase.auth.getSession()).data.session?.access_token || '';
                              const res = await fetch(`${edgeBase}/functions/v1/admin-chat-messages`, {
                                method: 'POST', headers: { 'content-type': 'application/json', Authorization: `Bearer ${token}` },
                                body: JSON.stringify({
                                  session_id: r.id,
                                  tenant_id: tenantId || undefined,
                                  site_id: siteId || ((typeof window !== 'undefined' && tenantId) ? (localStorage.getItem(`site_sel_${tenantId}`) || undefined) : undefined)
                                })
                              });
                              if (!res.ok) { setContactText('No contact info.'); return; }
                              const j = await res.json();
                              const msgs = Array.isArray(j.messages) ? j.messages : [];
                              const getContact = (m) => {
                                try {
                                  if (!m) return null;
                                  const a = m.analysis || {};
                                  if (a && a.contact && (a.contact.name || a.contact.email || a.contact.phone)) return a.contact;
                                  if (m.contact && (m.contact.name || m.contact.email || m.contact.phone)) return m.contact;
                                  if (m.response && m.response.contact && (m.response.contact.name || m.response.contact.email || m.response.contact.phone)) return m.response.contact;
                                  const c = m.content;
                                  if (typeof c === 'string') {
                                    const trimmed = c.trim();
                                    if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || trimmed.startsWith('"{')) {
                                      let obj = null;
                                      try { obj = JSON.parse(trimmed); } catch (_) { try { obj = JSON.parse(trimmed.replace(/^"|"$/g, '')); } catch (_) {} }
                                      if (obj) {
                                        if (obj.contact && (obj.contact.name || obj.contact.email || obj.contact.phone)) return obj.contact;
                                        if (obj.response && obj.response.contact && (obj.response.contact.name || obj.response.contact.email || obj.response.contact.phone)) return obj.response.contact;
                                      }
                                    }
                                  }
                                } catch (_) { /* ignore */ }
                                return null;
                              };
                              let name = null, email = null, phone = null;
                              for (let i = msgs.length - 1; i >= 0; i--) {
                                const m = msgs[i] || {};
                                const c = getContact(m);
                                if (c && (c.name || c.email || c.phone)) { name = c.name || name; email = c.email || email; phone = c.phone || phone; break; }
                              }
                              const lines = [];
                              if (name) lines.push(`Name: ${name}`);
                              if (email) lines.push(`Email: ${email}`);
                              if (phone) lines.push(`Phone: ${phone}`);
                              setContactText(lines.length ? lines.join('\n') : 'No contact info.');
                            } catch (_) {
                              setContactText('No contact info.');
                            }
                          }}>View</Button>
                      </OverlayTrigger>
                    );
                  })()}
                </td>
                <td><Button size="sm" variant="outline-primary" onClick={() => setDetail(r)}>View</Button></td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={7} className="text-muted">No chats in selected range.</td></tr>
            )}
          </tbody>
        </Table>
      )}

      <Modal show={!!detail} onHide={() => setDetail(null)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Chat Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {detail && <SessionMessages session={detail} edgeBase={edgeBase} />}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDetail(null)}>Close</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!contactSession} onHide={() => { setContactSession(null); setContactText(''); }} centered>
        <Modal.Header closeButton>
          <Modal.Title>Contact</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Control as="textarea" rows={5} readOnly value={contactText || 'No contact info.'} />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { setContactSession(null); setContactText(''); }}>Close</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

function SessionMessages({ session, edgeBase }) {
  const [msgs, setMsgs] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const { tenantId, siteId } = useTenant();
  React.useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const token = (await supabase.auth.getSession()).data.session?.access_token || '';
        const selectedSiteId = siteId || ((typeof window !== 'undefined' && tenantId)
          ? (localStorage.getItem(`site_sel_${tenantId}`) || '')
          : '');
        const res = await fetch(`${edgeBase}/functions/v1/admin-chat-messages`, {
          method: 'POST', headers: { 'content-type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            session_id: session.id,
            tenant_id: tenantId || undefined,
            site_id: selectedSiteId || undefined
          })
        });
        if (!res.ok) { setMsgs([]); return; }
        const j = await res.json();
        setMsgs(Array.isArray(j.messages) ? j.messages : []);
      } catch (_) { setMsgs([]); }
      setLoading(false);
    })();
  }, [session, edgeBase, tenantId, siteId]);

  if (loading) return <div className="text-muted">Loading…</div>;
  if (!msgs.length) return <div className="text-muted">No messages.</div>;

  // Helpers to extract plain user/assistant text and contact info from various shapes
  const getText = (m) => {
    try {
      if (!m) return '';
      // Prefer structured shapes
      if (m.response && typeof m.response.text === 'string') return m.response.text;
      if (m.text && typeof m.text === 'string') return m.text;
      // Content may be raw string or embedded JSON string
      if (typeof m.content === 'string') {
        const raw = m.content.trim();
        if ((raw.startsWith('{') && raw.endsWith('}')) || raw.startsWith('"{"')) {
          try {
            const obj = JSON.parse(raw);
            if (obj && obj.response && typeof obj.response.text === 'string') return obj.response.text;
            if (obj && typeof obj.text === 'string') return obj.text;
          } catch (_) {
            try {
              const obj2 = JSON.parse(raw.replace(/^"|"$/g, ''));
              if (obj2 && obj2.response && typeof obj2.response.text === 'string') return obj2.response.text;
              if (obj2 && typeof obj2.text === 'string') return obj2.text;
            } catch (_) { /* ignore */ }
          }
        }
        return raw;
      }
      return '';
    } catch (_) { return ''; }
  };
  const getContact = (m) => {
    try {
      if (!m) return null;
      const a = m.analysis || {};
      if (a && a.contact && (a.contact.name || a.contact.email || a.contact.phone)) return a.contact;
      if (m.contact && (m.contact.name || m.contact.email || m.contact.phone)) return m.contact;
      if (m.response && m.response.contact && (m.response.contact.name || m.response.contact.email || m.response.contact.phone)) return m.response.contact;
      if (typeof m.content === 'string') {
        const raw = m.content.trim();
        if ((raw.startsWith('{') && raw.endsWith('}')) || raw.startsWith('"{"')) {
          let obj = null;
          try { obj = JSON.parse(raw); } catch (_) { try { obj = JSON.parse(raw.replace(/^"|"$/g, '')); } catch (_) {} }
          if (obj) {
            if (obj.contact && (obj.contact.name || obj.contact.email || obj.contact.phone)) return obj.contact;
            if (obj.response && obj.response.contact && (obj.response.contact.name || obj.response.contact.email || obj.response.contact.phone)) return obj.response.contact;
          }
        }
      }
      return null;
    } catch (_) { return null; }
  };

  const contacts = [];
  const normalized = msgs.map((m) => {
    const roleLabel = String(m.role || '').toLowerCase() === 'assistant' ? 'SkinnyAI' : 'User';
    const text = getText(m);
    const c = getContact(m);
    if (c && (c.name || c.email || c.phone)) contacts.push({
      name: c.name || null,
      email: c.email || null,
      phone: c.phone || null,
    });
    return { ts: m.ts, roleLabel, text };
  });

  return (
    <div style={{ maxHeight: 400, overflow: 'auto' }}>
      {normalized.map((m, i) => (
        <div key={i} className="mb-3">
          <div className="small text-muted">{new Date(m.ts).toLocaleString()} · {m.roleLabel}</div>
          <div style={{ whiteSpace: 'pre-wrap' }}>{m.text}</div>
        </div>
      ))}
      {contacts.length > 0 && (
        <div className="mt-3">
          <div className="fw-semibold mb-2">Contact Info</div>
          <div style={{ whiteSpace: 'pre-wrap' }}>
            {contacts.map((c, idx) => (
              <div key={idx} className="mb-2">
                <div>Name: {c.name || '—'}</div>
                <div>Email: {c.email || '—'}</div>
                <div>Phone: {c.phone || '—'}</div>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* toast not applicable in this subview */}
    </div>
  );
}

function MySkinnyTab({ planId = '', onPlanChange }) {
  const { skinnyName, setSkinnyName, skinnyApiKey, brandColor, setBrandColor, brandTextColor, setBrandTextColor, settingsReady } = useSettings();
  const { user } = useAuth();
  const { tenantId: selectedTenantId, siteId: selectedSiteId } = useTenant();
  const [previewReloadTick, setPreviewReloadTick] = useState(0);
  const siteIdMemo = selectedSiteId || '';
  const [authToken, setAuthToken] = useState('');
  const [showCrawl, setShowCrawl] = useState(false);
  const [toastMsg, setToastMsg] = useState('');
  const [toastVariant, setToastVariant] = useState('success');
  const [agreeCrawl, setAgreeCrawl] = useState(false);
  const [crawlsRemaining, setCrawlsRemaining] = useState(null);
  const [position, setPosition] = useState('bottom-right'); // br, bl, tr, tl, advanced
  const [targetId, setTargetId] = useState('');
  const reportPlanChange = React.useCallback((pid) => {
    if (typeof onPlanChange === 'function') {
      onPlanChange(String(pid || ''));
    }
  }, [onPlanChange]);
  const [crawlStatus, setCrawlStatus] = useState('');
  const [crawlError, setCrawlError] = useState('');
  const [lastCrawledAt, setLastCrawledAt] = useState('');
  const activeSiteId = selectedSiteId || siteIdMemo || '';
  const [showAppearance, setShowAppearance] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [headerTitle, setHeaderTitle] = useState('Ask Skinny'); // used when setting theme from fetch/save
  const [launcherFillColor, setLauncherFillColor] = useState('#0d6efd');
  const [launcherTextColor, setLauncherTextColor] = useState('#ffffff');
  const [launcherText, setLauncherText] = useState('Ask Skinny');
  const [userBubbleColor, setUserBubbleColor] = useState('#0d6efd');
  const [userTextColor, setUserTextColor] = useState('#ffffff');
  const [replyColor, setReplyColor] = useState('#f3f4f6');
  const [replyTextColor, setReplyTextColor] = useState('#111111');
  const [headerGradientEnabled, setHeaderGradientEnabled] = useState(true);
  const [showAutoLaunch, setShowAutoLaunch] = useState(false);
  const [showQuickReplies, setShowQuickReplies] = useState(false);
  const [showCta, setShowCta] = useState(false);
  const fetchTheme = React.useCallback(async () => {
    try {
      if (!user?.id) return;
      if (!selectedSiteId) { debugLog('[MySkinnyTab] fetchTheme skip: no siteId'); return; }
      debugLog('[MySkinnyTab] fetchTheme', { siteId: selectedSiteId });
      const tok = (await supabase.auth.getSession()).data.session?.access_token || '';
      if (!tok) return;
      const edgeBase = process.env.REACT_APP_SKINNY_SUPABASE_URL;
      const res = await edgeFetch(`${edgeBase}/functions/v1/theme-get`, {
        method: 'POST',
        headers: { 'content-type': 'application/json', Authorization: `Bearer ${tok}` },
        body: JSON.stringify({ site_id: selectedSiteId }),
      });
      if (!res.ok) return;
      const j = await res.json();
      const t = j?.theme ? j.theme : j || {};
      const userBubble = t.accent_color || '#0d6efd';
      const lf = t.launcher_fill_color || t.accent_color || '#0d6efd';
      const lt = t.launcher_text_color || t.text_color || '#ffffff';
      const lname = t.launcher_text || t.chat_window_title || 'Ask Skinny';
      const htitle = t.chat_window_title || 'Ask Skinny';
      const userClr = t.user_text_color || t.text_color || '#ffffff';
      const botClr = t.reply_color || '#f3f4f6';
      const botText = t.reply_text_color || t.text_color || '#111111';
      const gradientFlag = typeof t.header_gradient === 'boolean' ? t.header_gradient : true;
      setLauncherFillColor(lf);
      setLauncherTextColor(lt);
      setLauncherText(lname);
      setHeaderTitle(htitle);
      setUserBubbleColor(userBubble);
      setUserTextColor(userClr);
      setReplyColor(botClr);
      setReplyTextColor(botText);
      setHeaderGradientEnabled(gradientFlag);
      // keep hidden settings in sync for other consumers
      setBrandColor(lf);
      setBrandTextColor(lt);
      setSkinnyName(lname);
    } catch (_) { /* ignore */ }
  }, [selectedSiteId, setBrandColor, setBrandTextColor, setSkinnyName, user?.id]);

  React.useEffect(() => {
    fetchTheme();
  }, [fetchTheme]);

  // Reload theme when dialog opens to hydrate from DB; do not overwrite on close to avoid clobbering fresh saves
  React.useEffect(() => {
    if (showAppearance) {
      fetchTheme();
    }
  }, [showAppearance, fetchTheme]);

  const { tenantBilling: ctxBilling } = useSettings();
  const billingStatus = ctxBilling?.billing_status ? String(ctxBilling.billing_status) : '';
  React.useEffect(() => {
    if (ctxBilling?.plan_id) {
      reportPlanChange(String(ctxBilling.plan_id));
    } else {
      reportPlanChange('');
    }
  }, [ctxBilling, reportPlanChange]);

  const widgetUrl = React.useMemo(() => {
    const cfg = process.env.REACT_APP_SKINNY_WIDGET_URL;
    if (cfg && cfg.trim()) return cfg.trim();
    return `${window.location.origin}/chatbot-widget.js`;
  }, []);

  const previewConfigRef = React.useRef('');
  const previewTimerRef = React.useRef(null);

  const previewConfig = React.useMemo(() => JSON.stringify({
    tenantId: selectedTenantId || '',
    siteId: selectedSiteId || '',
    launcherFillColor,
    launcherTextColor,
    launcherText,
    userBubbleColor,
    userTextColor,
    replyColor,
    replyTextColor,
    headerGradientEnabled,
    skinnyApiKey: skinnyApiKey || '',
    previewReloadTick,
  }), [
    selectedTenantId, selectedSiteId, launcherFillColor, launcherTextColor,
    launcherText, userBubbleColor, userTextColor, replyColor, replyTextColor,
    headerGradientEnabled, skinnyApiKey, previewReloadTick,
  ]);

  React.useEffect(() => {
    if (!settingsReady || !selectedSiteId) return;
    if (previewConfigRef.current === previewConfig) return;
    if (previewTimerRef.current) clearTimeout(previewTimerRef.current);
    previewTimerRef.current = setTimeout(async () => {
      previewConfigRef.current = previewConfig;
      const mount = document.getElementById('myskinny-preview');
      if (!mount) return;
      try {
        const oldPanels = document.querySelectorAll('[data-skinny-panel][data-skinny-scope="preview"]');
        oldPanels.forEach((el) => el.parentNode && el.parentNode.removeChild(el));
        const oldButtons = document.querySelectorAll('[data-skinny-button][data-skinny-scope="preview"]');
        oldButtons.forEach((el) => el.parentNode && el.parentNode.removeChild(el));
      } catch (_) {}
      mount.innerHTML = '';
      try {
        const tok = (await supabase.auth.getSession()).data.session?.access_token || '';
        setAuthToken(tok);
      } catch (_) {}
      const cfg = JSON.parse(previewConfig);
      debugLog('[Dashboard][preview] injecting widget', {
        selectedTenantId: cfg.tenantId,
        siteId: cfg.siteId,
        launcherFillColor: cfg.launcherFillColor,
        launcherText: cfg.launcherText,
      });
      const script = document.createElement('script');
      script.src = widgetUrl;
      if (cfg.tenantId) script.dataset.tenantId = cfg.tenantId;
      if (cfg.siteId) script.dataset.siteId = cfg.siteId;
      script.dataset.theme = 'light';
      script.dataset.target = 'myskinny-preview';
      script.dataset.scope = 'preview';
      script.dataset.apiEndpoint = `${window.location.origin}/.netlify/functions/proxy-fns/chat-complete`;
      if (authToken) script.dataset.authToken = authToken;
      const anon = process.env.REACT_APP_SKINNY_SUPABASE_ANON_KEY;
      if (anon) script.dataset.anonKey = anon;
      mount.appendChild(script);
    }, 150);
    return () => { if (previewTimerRef.current) clearTimeout(previewTimerRef.current); };
  }, [
    previewConfig,
    widgetUrl,
    authToken,
    settingsReady,
    selectedSiteId,
  ]);

  // Listen for debug meta from widget
  React.useEffect(() => {
    // Expose a global getter for a fresh auth token so the widget can avoid expired JWTs
    window.__skinny_getAuthToken = async () => {
      try {
        return (await supabase.auth.getSession()).data.session?.access_token || '';
      } catch (_) { return ''; }
    };
    return () => {
      try { delete window.__skinny_getAuthToken; } catch (_) {}
    };
  }, []);

  const InfoIcon = ({ text }) => (
    <OverlayTrigger placement="right" delay={{ show: 150, hide: 0 }} overlay={<Tooltip id="myskinny-tip" className="bg-dark">{text}</Tooltip>}>
      <button type="button" className="btn btn-outline-info btn-sm ms-1 p-0 d-inline-flex align-items-center justify-content-center rounded-circle" style={{ width: 18, height: 18, lineHeight: '16px' }} aria-label="Help">
        <i className="bi bi-info-circle" style={{ fontSize: 12 }} aria-hidden="true"></i>
      </button>
    </OverlayTrigger>
  );

  const isAdvanced = position === 'advanced';
  const isValidTargetId = React.useMemo(() => {
    if (!isAdvanced) return true;
    const v = (targetId || '').trim();
    if (!v) return false;
    return /^[A-Za-z][A-Za-z0-9_-]*$/.test(v);
  }, [targetId, isAdvanced]);

  const embedScript = React.useMemo(() => {
    const isProd = window.location.hostname.indexOf('theskinnyai.com') !== -1;
    const scriptSrc = isProd ? 'https://theskinnyai.com/chatbot-widget.js' : 'https://skinnyai.netlify.app/chatbot-widget.js';
    const apiEndpoint = isProd
      ? 'https://theskinnyai.com/.netlify/functions/proxy-fns/chat-complete'
      : 'https://skinnyai.netlify.app/.netlify/functions/proxy-fns/chat-complete';
    const baseAttrs = [
      `src="${scriptSrc}"`,
      `data-api-endpoint="${apiEndpoint}"`,
      `data-tenant-id="${selectedTenantId || ''}"`,
      selectedSiteId ? `data-site-id="${selectedSiteId}"` : '',
      `data-anon-key="${process.env.REACT_APP_SKINNY_SUPABASE_ANON_KEY || ''}"`,
    ].filter(Boolean).join('\n  ');
    if (isAdvanced && isValidTargetId) {
      return `<script ${baseAttrs}\n  data-target="${targetId}">\n</script>`;
    }
    const posMap = {
      'bottom-right': 'bottom-right',
      'bottom-left': 'bottom-left',
      'top-right': 'top-right',
      'top-left': 'top-left',
    };
    const pos = posMap[position] || 'bottom-right';
    const script = `<script ${baseAttrs}\n  data-position="${pos}">\n</script>`;
    return script;
  }, [
    selectedTenantId,
    selectedSiteId,
    position,
    isAdvanced,
    isValidTargetId,
    targetId,
  ]);

  const handleCopyEmbedScript = React.useCallback(async () => {
    if (!embedScript) return;
    try {
      const singleLine = embedScript.replace(/\s*\n\s*/g, ' ').replace(/\s{2,}/g, ' ').trim();
      await navigator.clipboard.writeText(singleLine);
      setToastMsg('Embed script copied to clipboard.');
    } catch (_) {
      setToastMsg('Unable to copy embed script.');
    }
  }, [embedScript]);

  const containerSnippet = React.useMemo(() => {
    if (!isAdvanced) return '';
    const id = (targetId || 'skinny-left-mid').trim();
    return `<div id="${id}" style="position:fixed; left:20px; top:50%; transform:translateY(-50%); z-index:2147480000;"></div>`;
  }, [isAdvanced, targetId]);

  // Helper to load user-visible crawl status
  React.useEffect(() => {
    setCrawlStatus('');
    setLastCrawledAt('');
    setCrawlError('');
  }, [selectedTenantId, selectedSiteId]);

  const fetchCrawlStatus = React.useCallback(async () => {
    try {
      const tok = (await supabase.auth.getSession()).data.session?.access_token || '';
      if (!tok) return;
      if (!selectedTenantId) {
        setCrawlStatus('');
        setLastCrawledAt('');
        setCrawlError('');
        return;
      }
      const edgeBase = process.env.REACT_APP_SKINNY_SUPABASE_URL;
      const headers = { 'content-type': 'application/json', Authorization: `Bearer ${tok}` };
      const selSite = activeSiteId;
      if (!selSite) {
        debugLog('[MySkinnyTab] crawlStatus skip: no siteId');
        setCrawlStatus('Pending');
        setLastCrawledAt('');
        setCrawlError('');
        return;
      }
      debugLog('[MySkinnyTab] crawlStatus fetch', { tenantId: selectedTenantId, siteId: selSite });
      const res = await edgeFetch(`${edgeBase}/functions/v1/user-crawl-status`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ tenant_id: selectedTenantId, site_id: selSite })
      });
      const txt = await res.text();
      if (!res.ok) {
        setCrawlError('Unable to load crawl status.');
        return;
      }
      let j = {};
      try { j = JSON.parse(txt); } catch {}
      const status = (j && typeof j.status === 'string') ? j.status : '';
      setCrawlStatus(status);
      if (j && j.last_crawled) setLastCrawledAt(j.last_crawled); else setLastCrawledAt('');
      const errorsCount = Number(j?.errors || 0);
      const systemErrs = Number(j?.system_errors || 0);
      const accessErrs = Number(j?.access_errors || 0);
      const successCount = Number(j?.success_count || 0);
      const pendingEmbed = Number(j?.pending_embed || 0);
      const showErrorBanner = (String(status || '').toLowerCase() === 'failed') || (errorsCount > 0 && successCount <= 0);
      if (showErrorBanner) {
        if (systemErrs > 0 && accessErrs === 0) {
          setCrawlError('Some pages could not be processed due to a temporary service issue. They will be retried automatically.');
        } else if (accessErrs > 0 && systemErrs === 0) {
          setCrawlError(`${accessErrs} page${accessErrs === 1 ? '' : 's'} could not be accessed (permission denied or not found). Check your site's access settings.`);
        } else if (systemErrs > 0 && accessErrs > 0) {
          setCrawlError(`${accessErrs} page${accessErrs === 1 ? ' is' : 's are'} inaccessible and ${systemErrs} had temporary processing errors (will retry).`);
        } else {
          setCrawlError('Some pages could not be crawled. They will be retried on the next run.');
        }
      } else if (pendingEmbed > 0) {
        setCrawlError(`${pendingEmbed} page${pendingEmbed === 1 ? ' is' : 's are'} waiting to be processed. This will complete on the next crawl cycle.`);
      } else {
        setCrawlError('');
      }
      try {
        const limit = Number(j?.crawls_limit ?? 0);
        const used = Number(j?.crawls_used_month ?? 0);
        if (!Number.isNaN(limit) && !Number.isNaN(used)) {
          setCrawlsRemaining(Math.max(0, limit - used));
        }
      } catch (_) {}
    } catch (_) {
      setCrawlError('Unable to load crawl status.');
    }
  }, [selectedTenantId, activeSiteId]);

  // Debounced crawl status: coalesce rapid dependency changes into a single fetch
  const crawlTimerRef = React.useRef(null);
  React.useEffect(() => {
    if (crawlTimerRef.current) clearTimeout(crawlTimerRef.current);
    crawlTimerRef.current = setTimeout(() => { fetchCrawlStatus(); }, 100);
    return () => { if (crawlTimerRef.current) clearTimeout(crawlTimerRef.current); };
  }, [fetchCrawlStatus, toastMsg, planId]);

  // Auto-refresh while an ephemeral crawl is active (Scheduled or In Progress)
  React.useEffect(() => {
    const active = (String(crawlStatus || '').toLowerCase() === 'scheduled') || (String(crawlStatus || '').toLowerCase() === 'in progress');
    if (!active) return;
    const id = setInterval(() => { fetchCrawlStatus(); }, 10000); // 10s polling while active
    return () => clearInterval(id);
  }, [crawlStatus, fetchCrawlStatus]);

  // Site selection lives in the header next to Tenant; scheduling reads from localStorage

  const tabReadyLatchRef = React.useRef({ tenant: '', ready: false });
  if (tabReadyLatchRef.current.tenant !== selectedTenantId) {
    tabReadyLatchRef.current = { tenant: selectedTenantId, ready: false };
  }
  if (!tabReadyLatchRef.current.ready && settingsReady && selectedSiteId) {
    tabReadyLatchRef.current.ready = true;
  }
  const tabReady = tabReadyLatchRef.current.ready;

  // Immediately tear down old widget elements when transitioning
  if (!tabReady) {
    try {
      const mount = document.getElementById('myskinny-preview');
      if (mount) mount.innerHTML = '';
      document.querySelectorAll('[data-skinny-panel][data-skinny-scope="preview"]').forEach(el => el.remove());
      document.querySelectorAll('[data-skinny-button][data-skinny-scope="preview"]').forEach(el => el.remove());
    } catch (_) {}
  }

  debugLog('[MySkinnyTab] render', { settingsReady, tabReady, selectedTenantId, selectedSiteId: selectedSiteId || '(empty)', billingStatus, crawlStatus, activeSiteId: activeSiteId || '(empty)' });

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-2">
        <div className="small">
          <span className="text-muted me-2">Billing Status:</span>
          <span className="fw-semibold text-capitalize">{billingStatus || 'Trial'}</span>
        </div>
      </div>
      <div className="mb-3">
        <div className="d-flex align-items-center gap-2 flex-wrap mb-2">
          <Button
            variant="primary"
            onClick={() => setShowCrawl(true)}
            disabled={
              !selectedTenantId ||
              !activeSiteId ||
              String(crawlStatus || '').toLowerCase() === 'scheduled' ||
              String(crawlStatus || '').toLowerCase() === 'in progress'
            }
          >
            Crawl My Site
          </Button>
          <InfoIcon text={
            'This will train your skinny from your site data. The crawler fetches pages in small batches every ~2 minutes. This button is disabled while a crawl is scheduled or in progress.'
          } />
        </div>
        <div className="small text-muted">
          <span className="me-2">Status: <span className="text-body">{crawlStatus || '—'}</span></span>
          <span className="me-3">Last Crawled: <span className="text-body">{lastCrawledAt ? new Date(lastCrawledAt).toLocaleString() : '—'}</span></span>
          <span>Crawls remaining this period: <span className="text-body">{typeof crawlsRemaining === 'number' ? crawlsRemaining : '—'}</span></span>
        </div>
        {crawlError ? (
          <div className={`small mt-1 ${crawlError.includes('waiting to be processed') || crawlError.includes('will retry') ? 'text-warning' : 'text-danger'}`}>{crawlError}</div>
        ) : null}
      </div>
      <Row className="g-3 mb-3">
        <Col md>
          <div className="border rounded p-4 bg-light d-flex" style={{ minHeight: 200 }}>
            <div style={{ flex: '0 0 280px' }}>
              {/* Placeholder mount point for widget preview */}
              <div id="myskinny-preview" />
            </div>
            <div className="ms-3 align-self-center text-muted">
              Your Skinny is on the left of this frame. Try it out!
            </div>
          </div>
        </Col>
      </Row>
      <div className="mb-3 d-flex align-items-center gap-2">
        <Button variant="outline-primary" size="sm" onClick={() => setShowAppearance(true)}>
          Appearance
        </Button>
        <InfoIcon text="Open the theme editor to customize your launcher and chat window colors, text, and shape. Changes apply immediately to your embed without touching your site code." />
        <Button variant="outline-dark" size="sm" onClick={() => setShowAutoLaunch(true)}>
          AutoGreet
        </Button>
        <InfoIcon text="Configure AutoGreet behavior: enable, delay, frequency, max impressions, and greeting messages." />
        <Button variant="outline-info" size="sm" onClick={() => setShowQuickReplies(true)}>
          Quick Replies
        </Button>
        <InfoIcon text="Configure suggestion buttons that appear in the chat widget. Visitors click a button to send a pre-defined message, guiding them toward demos, support, pricing, etc." />
        <Button variant="outline-success" size="sm" onClick={() => setShowCta(true)}>
          Calendar
        </Button>
        <InfoIcon text="Configure a call-to-action button that appears in chat when the AI detects buying intent. Link to your scheduling page." />
      </div>
      <div className="border rounded p-3 mb-3">
        <div className="fw-semibold mb-2">Launcher Button</div>
        <Form.Group className="mb-3" style={{ display: 'none' }}>
          <Form.Label>Button Text</Form.Label>
          <Form.Control value={skinnyName || ''} onChange={(e) => setSkinnyName(e.target.value)} placeholder="Ask Skinny" />
        </Form.Group>
        <Row className="g-2 mb-3" style={{ display: 'none' }}>
          <Col md>
            <Form.Label>Fill Color</Form.Label>
            <div className="d-flex align-items-center gap-2">
              <Form.Control type="color" value={brandColor || '#0d6efd'} onChange={(e)=> setBrandColor(e.target.value)} title="Fill color" />
              <Form.Control type="text" value={brandColor || ''} onChange={(e)=> {
                let v = e.target.value.trim();
                if (!v) { setBrandColor(''); return; }
                if (v[0] !== '#') v = '#'+v;
                v = v.replace(/[^#0-9a-fA-F]/g,'');
                if (v.length > 7) v = v.slice(0,7);
                if (/^#[0-9a-fA-F]{3}$/.test(v)) {
                  const r=v[1], g=v[2], b=v[3];
                  v = '#' + r+r + g+g + b+b;
                }
                setBrandColor(v);
              }} placeholder="#0d6efd" />
            </div>
          </Col>
          <Col md>
            <Form.Label>Text Color</Form.Label>
            <div className="d-flex align-items-center gap-2">
              <Form.Control type="color" value={brandTextColor || '#ffffff'} onChange={(e)=> setBrandTextColor(e.target.value)} title="Text color" />
              <Form.Control type="text" value={brandTextColor || ''} onChange={(e)=> {
                let v = e.target.value.trim();
                if (!v) { setBrandTextColor(''); return; }
                if (v[0] !== '#') v = '#'+v;
                v = v.replace(/[^#0-9a-fA-F]/g,'');
                if (v.length > 7) v = v.slice(0,7);
                if (/^#[0-9a-fA-F]{3}$/.test(v)) {
                  const r=v[1], g=v[2], b=v[3];
                  v = '#' + r+r + g+g + b+b;
                }
                setBrandTextColor(v);
              }} placeholder="#ffffff" />
            </div>
          </Col>
        </Row>
        <Form.Label>
          Position
          <InfoIcon text={isAdvanced ? 'Advanced: mount inside your own element using data-target. data-position is ignored when data-target is set.' : 'Choose a fixed corner position for the floating button.'} />
        </Form.Label>
        <Row className="g-2 mb-2">
          <Col md={4}>
            <Form.Select value={position} onChange={(e) => setPosition(e.target.value)} aria-label="Position">
              <option value="bottom-right">Bottom-right (recommended)</option>
              <option value="bottom-left">Bottom-left</option>
              <option value="top-right">Top-right</option>
              <option value="top-left">Top-left</option>
              <option value="advanced">Advanced…</option>
            </Form.Select>
          </Col>
          {isAdvanced && (
            <Col md>
              <Form.Label className="mb-1">Target element ID <InfoIcon text="data-target mounts the button inside your element and ignores data-position. Position it anywhere with CSS." /></Form.Label>
              <Form.Control
                placeholder="e.g., skinny-left-mid"
                value={targetId}
                onChange={(e) => setTargetId(e.target.value)}
                isInvalid={!isValidTargetId}
                aria-describedby="target-help"
              />
              <Form.Text id="target-help" muted>Must start with a letter; letters, numbers, hyphens, and underscores allowed.</Form.Text>
            </Col>
          )}
        </Row>
        {isAdvanced && (
          <div className="mb-3">
            <Form.Label>Optional container snippet</Form.Label>
            <Form.Control as="textarea" rows={2} readOnly value={containerSnippet} />
          </div>
        )}
      </div>

      <div className="mt-3">
        <div className="d-flex align-items-start justify-content-between gap-2">
          <Form.Label className="mb-0 flex-grow-1">
            Embed script
            <span className="ms-2 text-danger fw-semibold" style={{ fontSize: 12 }}>
              Important: Add TheSkinnyAI.com to your site's CORS/Content-Security-Policy so the widget can load and call the API.
              Allow scripts and connections to: https://theskinnyai.com
            </span>
            <InfoIcon text={
              'Production example (Netlify headers):\n' +
              '  [[headers]]\n' +
              '    for = "/*"\n' +
              '    [headers.values]\n' +
              '      Content-Security-Policy = "script-src \'self\' https://theskinnyai.com; connect-src \'self\' https://theskinnyai.com"\n\n' +
              'Or add equivalent rules in your server (e.g., Nginx) to allow script-src and connect-src for https://theskinnyai.com.'
            } />
          </Form.Label>
          <Button variant="outline-secondary" size="sm" onClick={handleCopyEmbedScript} disabled={!embedScript}>
            Copy script
          </Button>
        </div>
        <Form.Control as="textarea" rows={6} readOnly value={embedScript} />
        <Form.Text muted>
          Need to override colors or launcher text? Add optional <code>data-*</code> attributes as described in our&nbsp;
          <a href="https://theskinnyai.com/docs/site-themes#appearance-overrides" target="_blank" rel="noreferrer">Appearance Overrides FAQ</a>.
        </Form.Text>
      </div>
      

      <Modal show={showCrawl} onHide={() => setShowCrawl(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Schedule Site Crawl</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="small" style={{ lineHeight: 1.5 }}>
            <h6 className="fw-semibold mb-2">AI Response Disclosure and Terms of Service Agreement</h6>
            <p><strong>Disclosure</strong>: TheSkinnyAI is powered by advanced AI technology, which may produce unpredictable or unexpected responses. While we strive to provide tools (e.g., custom prompts, domain restrictions, and scraping configurations) to help steer the AI's behavior, the technology's nature means outcomes cannot be fully guaranteed. We recommend you review and adjust the provided settings to establish appropriate guardrails for your use case.</p>
            <p className="mb-1"><strong>Terms of Service</strong>: By checking the box below, you agree to the following terms:</p>
            <ul className="mb-2">
              <li>You acknowledge that TheSkinnyAI's responses are generated by AI and may not always align with your expectations.</li>
              <li>You are responsible for configuring the AI's settings (e.g., allowed domains, prompt instructions) to ensure compliance with your business needs, legal requirements, and ethical standards.</li>
              <li>You agree to indemnify, defend, and hold harmless the owners, operators, and affiliates of TheSkinnyAI (including xAI and its partners) from any claims, damages, or liabilities arising from your use of the chatbot, including but not limited to errors, inaccuracies, or misuse of AI-generated content.</li>
              <li>This agreement is governed by the laws of Maine, USA, and any disputes will be resolved through binding arbitration.</li>
            </ul>
            <p className="mb-2"><strong>Consent</strong>: Please check the box below to indicate your acceptance of these terms and proceed with the setup.</p>
          </div>
          <Form.Check type="checkbox" id="crawl-agree" label="I agree to the AI Response Disclosure and Terms of Service." checked={agreeCrawl} onChange={(e) => setAgreeCrawl(e.target.checked)} />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCrawl(false)}>Cancel</Button>
          <Button
            variant="primary"
            onClick={async () => {
              try {
                const { data } = await import('../lib/supabaseClient');
                const supabase = data?.supabase || (await import('../lib/supabaseClient')).supabase;
                const tok = (await supabase.auth.getSession()).data.session?.access_token || '';
                const edgeBase = process.env.REACT_APP_SKINNY_SUPABASE_URL;
                const headers = { 'content-type': 'application/json' };
                if (tok) headers['Authorization'] = `Bearer ${tok}`;
                const selSite = selectedSiteId || ((typeof window !== 'undefined' && selectedTenantId) ? (localStorage.getItem(`site_sel_${selectedTenantId}`) || '') : '');
                const payload = {
                  tenant_id: selectedTenantId || '',
                  site_id: selSite || '',
                  agree: true,
                  terms_version: '2025-10-04-v1'
                };
                const resp = await fetch(`${edgeBase}/functions/v1/user-site-request-crawl`, { method: 'POST', headers, body: JSON.stringify(payload) });
                const txt = await resp.text();
                let message = 'Crawl Scheduled';
                let scheduledOk = false;
                try {
                  const j = JSON.parse(txt || '{}');
                  if (j && j.disabled) {
                    message = String(j.message || 'Crawling is temporarily paused by an administrator. Please try again later.');
                  } else if (resp.status === 429 || j?.error === 'crawl_cap_reached') {
                    message = String(j?.details || 'Monthly crawl cap reached. Please upgrade.');
                  } else if (j && j.ok === true && j.scheduled) {
                    scheduledOk = true;
                    message = 'Crawl Scheduled';
                  } else if (!resp.ok) {
                    message = txt || `Schedule failed (${resp.status})`;
                  } else {
                    // Fallback success path
                    scheduledOk = true;
                  }
                } catch (_) {
                  if (!resp.ok) message = txt || `Schedule failed (${resp.status})`;
                  else scheduledOk = true;
                }
                setToastMsg(message);
                setToastVariant(scheduledOk ? 'success' : 'warning');
                if (scheduledOk) { try { setCrawlStatus('Scheduled'); } catch (_) {} }
              } catch (_) { /* ignore */ }
              setShowCrawl(false);
            }}
            disabled={!agreeCrawl || !selectedTenantId}
          >
            Schedule Crawl
          </Button>
        </Modal.Footer>
      </Modal>
      <AutoLaunchModal
        show={showAutoLaunch}
        onHide={() => setShowAutoLaunch(false)}
        onSave={() => {
          try { setShowAutoLaunch(false); } catch (_) {}
          try { setPreviewReloadTick((t) => t + 1); } catch (_) {}
          try { setToastMsg('AutoGreet saved. Preview reloaded.'); } catch (_) {}
        }}
        tenantId={selectedTenantId}
        siteId={selectedSiteId || siteIdMemo}
        admin={false}
      />
      <QuickRepliesModal
        show={showQuickReplies}
        onHide={() => setShowQuickReplies(false)}
        onSave={() => {
          try { setShowQuickReplies(false); } catch (_) {}
          try { setPreviewReloadTick((t) => t + 1); } catch (_) {}
          try { setToastMsg('Quick Replies saved. Preview reloaded.'); } catch (_) {}
        }}
        tenantId={selectedTenantId}
        siteId={selectedSiteId || siteIdMemo}
      />
      <CtaModal
        show={showCta}
        onHide={() => setShowCta(false)}
        onSave={() => {
          try { setShowCta(false); } catch (_) {}
          try { setToastMsg('CTA saved.'); } catch (_) {}
        }}
        tenantId={selectedTenantId}
        siteId={selectedSiteId || siteIdMemo}
      />
      <ToastContainer position="middle-center" className="p-3">
        <Toast onClose={() => setToastMsg('')} show={!!toastMsg} delay={toastVariant === 'success' ? 3000 : 5000} autohide bg={toastVariant}>
          <Toast.Body className="text-white">{toastMsg}</Toast.Body>
        </Toast>
      </ToastContainer>
      <React.Suspense fallback={null}>
        <ThemeEditorModal
          show={showAppearance}
          onHide={() => { setShowAppearance(false); fetchTheme(); }}
          onSave={(theme) => {
            if (theme) {
              const lf = theme.launcher_fill_color || theme.accent_color || '#0d6efd';
              const lt = theme.launcher_text_color || theme.text_color || '#ffffff';
              const lname = theme.launcher_text || theme.chat_window_title || 'Ask Skinny';
              const htitle = theme.chat_window_title || 'Ask Skinny';
              const userBubble = theme.accent_color || '#0d6efd';
              const userClr = theme.user_text_color || theme.text_color || '#ffffff';
              const botClr = theme.reply_color || '#f3f4f6';
              const botText = theme.reply_text_color || theme.text_color || '#111111';
              const gradientFlag = typeof theme.header_gradient === 'boolean' ? theme.header_gradient : true;
              setLauncherFillColor(lf);
              setLauncherTextColor(lt);
              setLauncherText(lname);
              setHeaderTitle(htitle);
              setUserBubbleColor(userBubble);
              setUserTextColor(userClr);
              setReplyColor(botClr);
              setReplyTextColor(botText);
              setHeaderGradientEnabled(gradientFlag);
              setBrandColor(lf);
              setBrandTextColor(lt);
              setSkinnyName(lname);
            }
            setShowAppearance(false);
          }}
          tenantId={selectedTenantId}
          siteId={selectedSiteId || siteIdMemo}
        />
      </React.Suspense>
    </div>
  );
}

function ShareTab() {
  const { tenantId, siteId } = useTenant();
  const [expires, setExpires] = useState('72'); // hours
  const [maxChats, setMaxChats] = useState(200);
  const [maxUsers, setMaxUsers] = useState(25);
  const [label, setLabel] = useState('Internal testing');
  const [generating, setGenerating] = useState(false);
  const [codes, setCodes] = useState([]);
  const [loadingList, setLoadingList] = useState(false);

  const edgeBase = process.env.REACT_APP_SKINNY_SUPABASE_URL;

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); alert('Copied to clipboard.'); } catch (_) { /* ignore */ }
  };

  const abortRef = React.useRef(null);

  const loadCodes = React.useCallback(async (signal) => {
    if (!tenantId) { debugLog('[ShareTab] skip: no tenantId'); setCodes([]); return; }
    debugLog('[ShareTab] loadCodes', { tenantId, siteId: siteId || '(empty)' });
    setLoadingList(true);
    try {
      const tok = (await supabase.auth.getSession()).data.session?.access_token || '';
      if (!tok) { setCodes([]); setLoadingList(false); return; }
      const body = tenantId ? { tenant_id: tenantId, site_id: siteId || undefined } : {};
      const res = await edgeFetch(`${edgeBase}/functions/v1/test-list-codes`, {
        method: 'POST', headers: { 'content-type': 'application/json', Authorization: `Bearer ${tok}` }, body: JSON.stringify(body),
        signal,
      });
      if (!res.ok) {
        debugLog('[ShareTab] error', { status: res.status, tenantId, siteId });
        setCodes([]); setLoadingList(false); return;
      }
      const j = await res.json();
      debugLog('[ShareTab] ok', { tenantId, codes: (j.codes || []).length });
      setCodes(Array.isArray(j.codes) ? j.codes : []);
    } catch (err) {
      if (err?.name === 'AbortError') { debugLog('[ShareTab] aborted'); return; }
      debugLog('[ShareTab] exception', { err: err?.message });
      setCodes([]);
    }
    setLoadingList(false);
  }, [edgeBase, tenantId, siteId]);

  React.useEffect(() => {
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    loadCodes(ctrl.signal);
    return () => ctrl.abort();
  }, [loadCodes]);

  async function generate() {
    setGenerating(true);
    try {
      const tok = (await supabase.auth.getSession()).data.session?.access_token || '';
      if (!tok) throw new Error('Not authenticated');
      const selectedSiteId = siteId || ((typeof window !== 'undefined' && tenantId)
        ? (localStorage.getItem(`site_sel_${tenantId}`) || '')
        : '');
      const body = {
        expires_in_hours: Number(expires) || 72,
        max_chats: Number(maxChats) || 0,
        max_users: Number(maxUsers) || 0,
        note: label || '',
        tenant_id: tenantId || undefined,
        site_id: selectedSiteId || undefined,
      };
      debugLog('[Share] create code', { tenantId });
      const res = await fetch(`${edgeBase}/functions/v1/test-create-code`, {
        method: 'POST', headers: { 'content-type': 'application/json', Authorization: `Bearer ${tok}` }, body: JSON.stringify(body)
      });
      const txt = await res.text();
      if (!res.ok) throw new Error(txt || 'Failed to create code');
      loadCodes();
    } catch (e) {
      alert(`Create failed: ${e.message || String(e)}`);
    }
    setGenerating(false);
  }

  async function revoke(id) {
    try {
      const tok = (await supabase.auth.getSession()).data.session?.access_token || '';
      if (!tok) return;
      const selectedSiteId = siteId || ((typeof window !== 'undefined' && tenantId)
        ? (localStorage.getItem(`site_sel_${tenantId}`) || '')
        : '');
      const res = await fetch(`${edgeBase}/functions/v1/test-revoke-code`, {
        method: 'POST',
        headers: { 'content-type': 'application/json', Authorization: `Bearer ${tok}` },
        body: JSON.stringify({ id, tenant_id: tenantId || undefined, site_id: selectedSiteId || undefined })
      });
      if (!res.ok) {
        const t = await res.text();
        alert(`Revoke failed: ${t}`);
        return;
      }
      loadCodes();
    } catch (e) {
      alert(`Revoke failed: ${e.message || String(e)}`);
    }
  }

  return (
    <div>
      <Row className="g-3 mb-3">
        <Col md={6}>
          <div className="border rounded p-3 bg-light">
            <div className="fw-semibold mb-2">Generate Test Access Code</div>
            <div className="small text-muted mb-2">Invite link opens our hosted test page with your Skinny preloaded. No embedding on external sites.</div>
            <Form.Group className="mb-2">
              <Form.Label>Label</Form.Label>
              <Form.Control value={label} onChange={(e)=>setLabel(e.target.value)} placeholder="Internal testing" />
            </Form.Group>
            <Row className="g-2">
              <Col md>
                <Form.Group className="mb-2">
                  <Form.Label>Expires In (hours)</Form.Label>
              <Form.Select value={expires} onChange={(e)=>setExpires(e.target.value)}>
                <option value="1">1</option>
                    <option value="24">24</option>
                    <option value="72">72</option>
                    <option value="168">168 (7 days)</option>
                    <option value="336">336 (14 days)</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md>
                <Form.Group className="mb-2">
                  <Form.Label>Max Chats</Form.Label>
                  <Form.Control type="number" min={0} value={maxChats} onChange={(e)=>setMaxChats(e.target.value)} />
                </Form.Group>
              </Col>
              <Col md>
                <Form.Group className="mb-2">
                  <Form.Label>Max Users</Form.Label>
                  <Form.Control type="number" min={0} value={maxUsers} onChange={(e)=>setMaxUsers(e.target.value)} />
                </Form.Group>
              </Col>
            </Row>
            <div className="d-flex gap-2 mt-2">
              <Button onClick={generate} disabled={generating}>Generate</Button>
            </div>
            {/* Details removed per request: no code or invitation URL shown here */}
          </div>
        </Col>
        <Col md={6}>
          <div className="border rounded p-3 bg-light">
            <div className="fw-semibold mb-2">Active Test Codes</div>
            {loadingList ? (
              <div className="text-muted">Loading…</div>
            ) : (
              <Table size="sm" responsive>
                <thead>
                  <tr>
                    <th>Label</th>
                    <th>Code</th>
                    <th>Expires</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {codes.map((c, i) => (
                    <tr key={c.id || i}>
                      <td>{c.note || '—'}</td>
                      <td className="text-monospace">{(c.code||'').slice(0,4)}…{(c.code||'').slice(-4)}</td>
                      <td>{c.expires_at ? new Date(c.expires_at).toLocaleString() : '—'}</td>
                      <td className="d-flex gap-2">
                        <Button size="sm" variant="outline-secondary" onClick={()=> copy(`${window.location.origin}/test?code=${encodeURIComponent(c.code||'')}`)}>Copy Link</Button>
                        <Button size="sm" variant="outline-danger" onClick={()=> revoke(c.id)}>Revoke</Button>
                      </td>
                    </tr>
                  ))}
                  {codes.length === 0 && (
                    <tr><td colSpan={4} className="text-muted">No active test codes.</td></tr>
                  )}
                </tbody>
              </Table>
            )}
          </div>
        </Col>
      </Row>
    </div>
  );
}

function BillingTab({ refreshTick = 0, onRefresh, onBillingInfo }) {
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');
  const [statusMsg, setStatusMsg] = React.useState('');
  const edgeBase = process.env.REACT_APP_SKINNY_SUPABASE_URL;
  const navigate = useNavigate();
  const [billing, setBilling] = React.useState(null);
  const { tenantId } = useTenant();
  const { tenantBilling: ctxBillingData, runtimeFlags: ctxRuntimeFlags } = useSettings();
  const [showPlan, setShowPlan] = React.useState(false);
  const [planId, setPlanId] = React.useState('team'); // default
  const [includeSetup, setIncludeSetup] = React.useState(false); // optional concierge setup
  const [showManage, setShowManage] = React.useState(false);
  const [showConfirmUpgrade, setShowConfirmUpgrade] = React.useState(false);
  const [confirmTarget, setConfirmTarget] = React.useState(null); // 'team' | 'enterprise'
  const [showConfirmCancel, setShowConfirmCancel] = React.useState(false);
  const [showConfirmDowngrade, setShowConfirmDowngrade] = React.useState(false);
  const [confirmDowngradeTarget, setConfirmDowngradeTarget] = React.useState(null); // 'pro' | 'entry'
  const nextPlanId = React.useMemo(() => {
    try { return String(billing?.next_plan_id || '').toLowerCase(); } catch (_) { return ''; }
  }, [billing]);
  
  const trialDaysRemaining = React.useMemo(() => {
    try {
      if (!billing?.trial_end) return null;
      const end = new Date(billing.trial_end);
      if (Number.isNaN(end.getTime())) return null;
      const diff = Math.ceil((end.getTime() - Date.now()) / (24 * 60 * 60 * 1000));
      return Math.max(diff, 0);
    } catch (_) {
      return null;
    }
  }, [billing]);

  // Normalize legacy plan ids to new names for UI comparisons
  // Resolve a user-friendly plan display name from billing payload
  const planDisplayName = React.useMemo(() => {
    const raw = String(billing?.plan_id || billing?.plan || '').toLowerCase();
    if (raw === 'trial') {
      return trialDaysRemaining !== null ? `Trial (${trialDaysRemaining} days left)` : 'Trial';
    }
    if (raw === 'entry' || raw === 'starter') return 'Legacy Plan';
    if (raw === 'basic' || raw === 'professional' || raw === 'pro') return 'Pro';
    if (raw === 'team') return 'Team';
    if (raw === 'enterprise' || raw === 'platinum') return 'Enterprise';
    return '';
  }, [billing, trialDaysRemaining]);

  // Normalize current plan id (handles legacy and new ids consistently)
  const currentPlanIdNormalized = React.useMemo(() => {
    const raw = String(billing?.plan_id || billing?.plan || '').toLowerCase();
    if (raw === 'trial') return 'trial';
    if (raw === 'entry' || raw === 'starter') return 'legacy';
    if (raw === 'basic' || raw === 'professional' || raw === 'pro') return 'pro';
    if (raw === 'team') return 'team';
    if (raw === 'enterprise' || raw === 'platinum') return 'enterprise';
    return '';
  }, [billing]);
  const normalizedNextPlanId = React.useMemo(() => {
    if (nextPlanId === 'trial') return 'trial';
    if (nextPlanId === 'entry' || nextPlanId === 'starter') return 'legacy';
    if (nextPlanId === 'basic' || nextPlanId === 'professional' || nextPlanId === 'pro') return 'pro';
    if (nextPlanId === 'platinum') return 'enterprise';
    return nextPlanId;
  }, [nextPlanId]);
  const planUsedForUpgradeCtas = normalizedNextPlanId || currentPlanIdNormalized;

  // Reset concierge setup selection when plan selection changes in Choose Plan modal
  React.useEffect(() => {
    setIncludeSetup(false);
  }, [planId]);

  // Reset includeSetup when opening either modal to avoid stale carryover
  React.useEffect(() => {
    if (showPlan) setIncludeSetup(false);
  }, [showPlan]);
  React.useEffect(() => {
    if (showManage) setIncludeSetup(false);
  }, [showManage]);
  // Also reset when exiting the confirm-upgrade flow (modal closed)
  React.useEffect(() => {
    if (!showConfirmUpgrade) setIncludeSetup(false);
  }, [showConfirmUpgrade]);


  function PlanPreview({ plan }) {
    const commonCardProps = { className: `h-100 shadow-sm ${disabledPlans[plan] ? 'opacity-50' : ''}` };
    if (plan === 'pro') {
      return (
        <Card {...commonCardProps}>
          <Card.Body className="p-4 d-flex flex-column h-100">
          <div className="d-flex align-items-center justify-content-between">
            <h2 className="h4 fw-bold mb-1">Pro</h2>
          </div>
          <div className="text-muted mb-2">Turn every visitor into a lead</div>
          <div className="d-flex align-items-baseline gap-2 mb-1">
            <div className="display-6 fw-bold text-primary">$199.99</div>
            <div className="small text-muted">USD</div>
          </div>
            <div className="text-muted mb-3">Per month</div>
            <ul className="mb-0">
              <li>Seats: 1</li>
              <li>Sites: 1</li>
            <li>Crawls per month: 15</li>
            <li>Indexed pages: 200 per site</li>
            <li>Max tokens per page: 10,000</li>
            <li>Chat messages: 10,000 / month</li>
            </ul>
          </Card.Body>
        </Card>
      );
    }
    if (plan === 'team') {
      return (
        <Card {...commonCardProps} className={`h-100 shadow-sm border-primary ${disabledPlans[plan] ? 'opacity-50' : ''}`}>
          <Card.Body className="p-4 d-flex flex-column h-100">
          <div className="d-flex align-items-center justify-content-between">
            <h2 className="h4 fw-bold mb-1">Team</h2>
            <span className="badge-team">Most Popular</span>
            </div>
          <div className="text-muted mb-2">Scale leads across teams</div>
            <div className="d-flex align-items-baseline gap-2 mb-1">
              <div className="display-6 fw-bold text-primary">$399.99</div>
              <div className="small text-muted">USD</div>
            </div>
            <div className="text-muted mb-3">Per month</div>
          <ul className="mb-0">
            <li>Seats: 5</li>
            <li>Sites: 1</li>
            <li>Crawls per month: 30</li>
          <li>Indexed pages: 500 per site</li>
          <li>Max tokens per page: 20,000</li>
          <li>Chat messages: 25,000 / month</li>
            <li>Analysis tab: Enabled</li>
          <li>Optional concierge setup available</li>
          </ul>
        </Card.Body>
        </Card>
      );
    }
    // default to enterprise
    return (
      <Card {...commonCardProps}>
        <Card.Body className="p-4 d-flex flex-column h-100">
          <div className="d-flex align-items-center justify-content-between">
            <h2 className="h4 fw-bold mb-1">Enterprise</h2>
          <span className="badge-enterprise">Expandable</span>
          </div>
        <div className="text-muted mb-2">SEO/Site Management Teams/Agencies</div>
        <div className="d-flex align-items-baseline gap-2 mb-1">
          <div className="display-6 fw-bold text-primary">$1,499.99</div>
          <div className="small text-muted">USD</div>
        </div>
          <div className="text-muted mb-3">Per month</div>
          <ul className="mb-0">
            <li>Seats: 10</li>
            <li>Sites: 5</li>
            <li>Crawls per month: 150</li>
          <li>Indexed pages: 500 per site</li>
          <li>Max tokens per page: 20,000</li>
            <li>Chat messages: 25,000 / month</li>
          <li>Analysis tab: Enabled</li>
          <li>Optional concierge setup available</li>
          </ul>
        </Card.Body>
      </Card>
    );
  }
const [disabledPlans, setDisabledPlans] = React.useState({
  pro: false,
  team: false,
  enterprise: false,
});
  const hasSubscription = React.useMemo(() => {
    try {
      const status = String(billing?.billing_status || '').toLowerCase();
      // Consider subscription "present" only for non-canceled states
      return ['active', 'trialing', 'past_due', 'incomplete'].includes(status);
    } catch (_) {
      return false;
    }
  }, [billing]);
  const isTrialPlan = React.useMemo(() => {
    if (!billing) return false;
    const status = String(billing.billing_status || '').toLowerCase();
    return currentPlanIdNormalized === 'trial' || status === 'trialing';
  }, [billing, currentPlanIdNormalized]);
  const canManageSubscription = hasSubscription && !isTrialPlan;

  React.useEffect(() => {
    // Read status from URL
    const params = new URLSearchParams(window.location.search);
    const status = params.get('status');
    if (status) {
      setStatusMsg(status);
      params.delete('status');
      const newSearch = params.toString();
      const newUrl = `${window.location.pathname}${newSearch ? `?${newSearch}` : ''}${window.location.hash || ''}`;
      try { window.history.replaceState({}, '', newUrl); } catch (_) { /* ignore */ }
    }
  }, []);

  React.useEffect(() => {
    if (ctxBillingData) {
      setBilling(ctxBillingData);
      try {
        if (typeof onBillingInfo === 'function') {
          onBillingInfo({ isProBono: !!(ctxBillingData.billing_override_allow) });
        }
      } catch (_) { /* ignore */ }
    }
    const pd = ctxRuntimeFlags?.plans_disabled;
    if (pd && typeof pd === 'object') {
      setDisabledPlans({
        pro: !!(pd.pro ?? pd.basic ?? pd.professional),
        team: !!pd.team,
        enterprise: !!pd.enterprise,
      });
    } else {
      setDisabledPlans({ pro: false, team: false, enterprise: false });
    }
  }, [ctxBillingData, ctxRuntimeFlags, onBillingInfo]);

  // Helpers: fetch and poll tenant billing until predicate satisfied or timeout
  const fetchBillingOnce = React.useCallback(async () => {
    try {
      const tok = (await supabase.auth.getSession()).data.session?.access_token || '';
      if (!tok) return null;
      const qs = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : '';
      const res = await fetch(`${edgeBase}/functions/v1/settings-get${qs}`, { headers: { Authorization: `Bearer ${tok}` } });
      if (!res.ok) return null;
      const j = await res.json();
      const payload = j?.tenant_billing || null;
      try {
        if (typeof onBillingInfo === 'function') {
          onBillingInfo({ isProBono: !!(payload && payload.billing_override_allow) });
        }
      } catch (_) { /* ignore */ }
      return payload;
    } catch (_) {
      return null;
    }
  }, [edgeBase, tenantId, onBillingInfo]);

  async function pollBillingUntil(predicate, timeoutMs = 15000, intervalMs = 1000) {
    const start = Date.now();
    // immediately refresh once
    const first = await fetchBillingOnce();
    if (first) {
      setBilling(first);
      if (predicate(first)) return true;
    }
    while (Date.now() - start < timeoutMs) {
      await new Promise(r => setTimeout(r, intervalMs));
      const b = await fetchBillingOnce();
      if (b) {
        setBilling(b);
        if (predicate(b)) return true;
      }
    }
    return false;
  }

  async function go(path, body = {}) {
    setError('');
    setBusy(true);
    try {
      const tok = (await supabase.auth.getSession()).data.session?.access_token || '';
      if (!tok) throw new Error('Not authenticated');
      const res = await fetch(`${edgeBase}/functions/v1/${path}`, {
        method: 'POST',
        headers: { 'content-type': 'application/json', Authorization: `Bearer ${tok}` },
        body: JSON.stringify(body),
      });
      const txt = await res.text();
      if (!res.ok) throw new Error(txt || `Failed: ${path}`);
      let j = {};
      try { j = JSON.parse(txt); } catch (_) {}
      const url = j.url || j.portal_url || '';
      if (!url) throw new Error('No redirect URL returned');
      window.location.href = url;
    } catch (e) {
      setError(e.message || String(e));
    }
    setBusy(false);
  }

  function startUpgrade(targetPlanId) {
    setConfirmTarget(targetPlanId);
    setShowConfirmUpgrade(true);
  }

  function startDowngrade(targetPlanId) {
    setConfirmDowngradeTarget(targetPlanId);
    setShowConfirmDowngrade(true);
  }

  async function performUpgrade() {
    if (!confirmTarget) { setShowConfirmUpgrade(false); return; }
    setShowConfirmUpgrade(false);
    setBusy(true);
    try {
      const tok = (await supabase.auth.getSession()).data.session?.access_token || '';
      const res = await fetch(`${edgeBase}/functions/v1/upgrade-subscription`, {
        method: 'POST',
        headers: { 'content-type': 'application/json', Authorization: `Bearer ${tok}` },
        body: JSON.stringify({ target_plan_id: confirmTarget, effective: 'immediate', tenant_id: tenantId, include_setup: includeSetup }),
      });
      const txt = await res.text();
      let j = {};
      try { j = JSON.parse(txt); } catch (_) {}
      const url = j.url || '';
      if (url) { window.location.href = url; return; }
      // No redirect: poll until plan becomes the target (immediate)
      await pollBillingUntil((b) => String(b?.plan_id || '') === String(confirmTarget), 15000, 1000);
      if (typeof onRefresh === 'function') onRefresh();
    } catch (e) {
      setError(e.message || String(e));
    }
    setBusy(false);
  }
  return (
    <div>
      <div className="border rounded p-3 bg-light mb-3">
        <div className="fw-semibold mb-2">Billing</div>
        <div className="text-muted small mb-3">Start your subscription, or manage billing.</div>
        {/* Surface redirect status from query params (e.g., ?status=success|canceled) */}
        {(() => {
          if (!statusMsg) return null;
          const ok = statusMsg === 'success';
          return <div className={`alert ${ok ? 'alert-success' : 'alert-warning'} py-2`} role="alert">{ok ? 'Payment completed.' : 'Checkout canceled.'}</div>;
        })()}
        {error ? <div className="alert alert-danger py-2" role="alert">{error}</div> : null}
        {/* Account status summary */}
        <div className="mb-3">
          <div className="small text-muted">Account Status</div>
          {billing?.billing_override_allow ? (
            <div>
              <span className="fw-semibold text-success">Pro Bono Account</span>
            </div>
          ) : null}
          <div>
            <span className="me-2">Concierge Setup:</span>
            <span className="fw-semibold">{billing?.premium_setup_paid_at ? 'Paid' : 'Not paid'}</span>
            {billing?.premium_setup_paid_at ? <span className="text-muted ms-2 small">({new Date(billing.premium_setup_paid_at).toLocaleString()})</span> : null}
          </div>
          <div className="mt-1">
            <span className="me-2">Subscription:</span>
            <span className="fw-semibold text-capitalize">
              {planDisplayName ? `${planDisplayName} — ${billing?.billing_cycle ? String(billing.billing_cycle) : 'Monthly'}` : '—'}
            </span>
          </div>
          <div className="mt-1">
            <span className="me-2">Status:</span>
            <span className="fw-semibold text-capitalize">{billing?.billing_status || '—'}</span>
            {billing?.billing_status === 'active' && billing?.cancel_at_period_end ? (
              <span className="text-muted ms-2 small">
                Ends: {new Date(billing?.cancel_at || billing?.current_period_end || '').toLocaleString()}
              </span>
            ) : (billing?.billing_status === 'active' && billing?.current_period_end ? (
              <span className="text-muted ms-2 small">Renews: {new Date(billing.current_period_end).toLocaleString()}</span>
            ) : null)}
            {billing?.billing_status === 'trialing' && billing?.trial_end ? (
              <span className="text-muted ms-2 small">Trial ends: {new Date(billing.trial_end).toLocaleString()}</span>
            ) : null}
            {billing?.billing_status === 'canceled' ? (
              <>
                {billing?.canceled_at ? (
                  <span className="text-muted ms-2 small">Ended: {new Date(billing.canceled_at).toLocaleString()}</span>
                ) : billing?.current_period_end ? (
                  <span className="text-muted ms-2 small">Ended: {new Date(billing.current_period_end).toLocaleString()}</span>
                ) : null}
              </>
            ) : null}
          </div>
          <div className="mt-1">
            <span className="me-2">Next renewal:</span>
            <span className="fw-semibold">
              {billing?.next_start_at ? new Date(billing.next_start_at).toLocaleString() : '—'}, {billing?.cancel_at_period_end ? 'Subscription ending' : (billing?.next_plan_id ? (String(billing.next_plan_id).charAt(0).toUpperCase() + String(billing.next_plan_id).slice(1)) : 'None')}
            </span>
          </div>
        </div>
        
      </div>
      <div className="text-muted small">Note: When you join today, you will be redirected to Stripe Checkout or the Customer Portal. After completion, you'll be returned to this dashboard.</div>
      {/* Choose Plan Modal */}
      <Modal show={showPlan} onHide={() => setShowPlan(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Choose Plan</Modal.Title>
        </Modal.Header>
        <Modal.Body>
        <style>{`
            .badge-team{
              position: relative;
              display: inline-block;
              padding: .35em .65em;
              border-radius: 10rem;
              font-weight: 600;
              font-size: .75rem;
              line-height: 1;
              color: #ffffff;
              background: linear-gradient(180deg,#b3d4ff 0%,#3b82f6 55%,#1d4ed8 100%);
              border: 1px solid rgba(37,99,235,.6);
              box-shadow: inset 0 1px rgba(255,255,255,.7);
              overflow: hidden;
            }
            .badge-team::after{
              content: "";
              position: absolute;
              inset: 0;
              background: linear-gradient(120deg, transparent 0%, rgba(255,255,255,.28) 8%, rgba(255,255,255,.55) 16%, rgba(255,255,255,.28) 24%, transparent 36%);
              transform: translateX(-120%);
              animation: shimmer-slow 12s linear infinite;
            }
            .badge-enterprise{
              position: relative;
              display: inline-block;
              padding: .35em .65em;
              border-radius: 10rem;
              font-weight: 600;
              font-size: .75rem;
              line-height: 1;
              color: #ffffff;
              background: linear-gradient(180deg,#cbd5e1 0%,#94a3b8 55%,#e2e8f0 100%);
              border: 1px solid rgba(100,116,139,.6);
              box-shadow: inset 0 1px rgba(255,255,255,.7);
              overflow: hidden;
            }
            .badge-enterprise::after{
              content: "";
              position: absolute;
              inset: 0;
              background: linear-gradient(120deg, transparent 0%, rgba(255,255,255,.22) 10%, rgba(255,255,255,.48) 17%, rgba(255,255,255,.22) 24%, transparent 36%);
              transform: translateX(-120%);
              animation: shimmer-slow 12s linear infinite;
            }
            .badge-popular{
              display: inline-block;
              padding: .35em .65em;
              border-radius: 10rem;
              font-weight: 600;
              font-size: .75rem;
              line-height: 1;
              color: #ffffff;
              background: linear-gradient(180deg,#28a745 0%, #198754 60%, #157347 100%);
              border: 1px solid rgba(21,115,71,.7);
              box-shadow: inset 0 1px rgba(255,255,255,.7);
            }
            @keyframes shimmer-slow{
              from { transform: translateX(-120%); }
              to   { transform: translateX(120%); }
            }
            @media (prefers-reduced-motion: reduce){
              .badge-enterprise::after, .badge-gold::after { animation: none; }
            }
          `}</style>
          <Form>
            <div className="d-flex align-items-center justify-content-between mb-2">
              <Form.Check
                type="radio"
                id="plan-pro"
                name="plan"
                label="Pro"
                checked={planId === 'pro'}
                disabled={disabledPlans.pro}
                onChange={() => setPlanId('pro')}
              />
              {disabledPlans.pro && (
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => { setShowPlan(false); navigate('/contact?message=' + encodeURIComponent('Join Pro Waitlist')); }}
                >
                  Join Wait List
                </Button>
              )}
            </div>
            <div className="d-flex align-items-center justify-content-between mb-2">
              <Form.Check
                type="radio"
                id="plan-team"
                name="plan"
                label="Team"
                checked={planId === 'team'}
                disabled={disabledPlans.team}
                onChange={() => setPlanId('team')}
              />
              {disabledPlans.team && (
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => { setShowPlan(false); navigate('/contact?message=' + encodeURIComponent('Join Team Waitlist')); }}
                >
                  Join Wait List
                </Button>
              )}
            </div>
            <div className="d-flex align-items-center justify-content-between">
              <Form.Check
                type="radio"
                id="plan-enterprise"
                name="plan"
                label="Enterprise"
                checked={planId === 'enterprise'}
                disabled={disabledPlans.enterprise}
                onChange={() => setPlanId('enterprise')}
              />
              {disabledPlans.enterprise && (
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => { setShowPlan(false); navigate('/contact?message=' + encodeURIComponent('Join Enterprise Waitlist')); }}
                >
                  Join Wait List
                </Button>
              )}
            </div>
          </Form>
          {(planId === 'team' || planId === 'enterprise') && (
            <div className="mt-3 border rounded p-2 bg-light">
              <Form.Check
                type="checkbox"
                id="include-setup"
                label="Include Concierge Setup (includes 30‑day free trial)"
                checked={includeSetup}
                onChange={(e)=>setIncludeSetup(e.target.checked)}
              />
              <div className="form-text">
                Concierge includes tailored onboarding and best‑practice tuning. Trial applies when setup is included.
              </div>
            </div>
          )}
          <div className="mt-3">
            <PlanPreview plan={planId} />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPlan(false)}>Cancel</Button>
          <Button variant="primary" disabled={busy || !!disabledPlans[planId]} onClick={() => { setShowPlan(false); go('create-checkout-join-today', { plan_id: planId, tenant_id: tenantId, include_setup: includeSetup }); }}>
            Continue
          </Button>
        </Modal.Footer>
      </Modal>
      {/* Manage Subscription Modal */}
      <Modal show={showManage} onHide={() => setShowManage(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Manage Subscription</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <div className="small text-muted">Current status</div>
            <div className="fw-semibold text-capitalize">
              {billing?.billing_status || '—'}
              {planDisplayName ? ` — ${planDisplayName}` : ''}
            </div>
            {billing?.current_period_end ? (
              <div className="text-muted small">Renews: {new Date(billing.current_period_end).toLocaleString()}</div>
            ) : null}
            {billing?.trial_end ? (
              <div className="text-muted small">Trial ends: {new Date(billing.trial_end).toLocaleString()}</div>
            ) : null}
          </div>
          <div className="d-grid gap-2">
            <Button
              variant="outline-secondary"
              disabled={busy || normalizedNextPlanId === 'enterprise' || disabledPlans.enterprise}
              onClick={() => { setShowManage(false); startUpgrade('enterprise'); }}
            >
              Upgrade to Enterprise
            </Button>
            <Button
              variant={planUsedForUpgradeCtas === 'team' ? 'outline-danger' : 'outline-primary'}
              disabled={busy || normalizedNextPlanId === 'team' || disabledPlans.team}
              onClick={() => {
                setShowManage(false);
                if (planUsedForUpgradeCtas === 'team') {
                  startDowngrade('team');
                } else {
                  startUpgrade('team');
                }
              }}
            >
              {planUsedForUpgradeCtas === 'team' ? 'Downgrade to Team' : 'Upgrade to Team'}
            </Button>
            <Form.Group className="mt-2">
              <Form.Check
                type="checkbox"
                id="manage-include-concierge-setup"
                label="Add Concierge Setup (includes 30‑day free trial)"
                checked={includeSetup}
                onChange={(e) => setIncludeSetup(!!(e && e.target && e.target.checked))}
              />
              <div className="form-text">
                Applies only when upgrading to Team or Enterprise. Billed as a one‑time setup fee.
              </div>
            </Form.Group>
            {(() => {
              const isTeamOrEnterprise = currentPlanIdNormalized === 'team' || currentPlanIdNormalized === 'enterprise';
              const hasConcierge = !!billing?.premium_setup_paid_at;
              if (!isTeamOrEnterprise || hasConcierge) return null;
              return (
                <div className="border rounded p-2 bg-light">
                  <div className="fw-semibold mb-1">Concierge Setup & Coaching</div>
                  <div className="text-muted small mb-2">
                    One-time setup to tailor configuration, ingest content, and tune best practices. Billed separately.
                  </div>
                  <div className="d-grid">
                    <Button
                      variant="warning"
                      disabled={busy}
                      onClick={() => { setShowManage(false); go('create-checkout-session-one-time', { tenant_id: tenantId }); }}
                    >
                      Purchase Concierge Setup
                    </Button>
                  </div>
                </div>
              );
            })()}
            <Button
              variant="outline-success"
              disabled={busy || normalizedNextPlanId === 'pro' || disabledPlans.pro}
              onClick={async () => {
                setShowManage(false);
                startDowngrade('pro');
              }}
            >
              Downgrade to Pro
            </Button>
            <div className="text-muted small">
            Setup Fees are nonrefundable. Upgrade plans take effect immediately. Downgrades take effect at the end of your current billing period. No free month or setup fee is applied for Pro (legacy plans follow the same rule).
            </div>
            <hr />
            <div className="mb-2 fw-semibold">Cancel plan (end of period)</div>
            <div className="text-muted small mb-2">
              Canceling now will keep your subscription active until your current period ends. After that date, if usage exceeds your free/selected tier, excess content will be paused. You can resume anytime by re‑subscribing.
            </div>
            <Button
              variant="danger"
              disabled={busy}
              onClick={() => { setShowManage(false); setShowConfirmCancel(true); }}
            >
              Cancel plan
            </Button>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowManage(false)}>Close</Button>
        </Modal.Footer>
      </Modal>
      {/* Confirm Upgrade Modal */}
      <Modal show={showConfirmUpgrade} onHide={() => setShowConfirmUpgrade(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Upgrade</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-2">
            You are about to upgrade to{' '}
            <strong>{confirmTarget ? (String(confirmTarget).charAt(0).toUpperCase() + String(confirmTarget).slice(1)) : ''}</strong>.
          </div>
          <ul className="mb-2">
            <li>Upgrades take effect immediately with proration.</li>
            {(['team','enterprise'].includes(String(confirmTarget || '').toLowerCase())) ? (
              includeSetup ? (
                <>
                  <li>Concierge Setup will be added as a one‑time purchase.</li>
                  <li>You may be redirected to Stripe Checkout to complete payment.</li>
                  <li>Setup fees are nonrefundable.</li>
                </>
              ) : (
                <li>Concierge Setup will not be added.</li>
              )
            ) : null}
          </ul>
          <div className="text-muted small">Proceed with the upgrade?</div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmUpgrade(false)}>Cancel</Button>
          <Button variant="primary" disabled={busy || !confirmTarget} onClick={performUpgrade}>Upgrade</Button>
        </Modal.Footer>
      </Modal>
      {/* Confirm Downgrade Modal */}
      <Modal show={showConfirmDowngrade} onHide={() => setShowConfirmDowngrade(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Downgrade</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-2">
            You are about to switch to{' '}
            <strong>{confirmDowngradeTarget ? (String(confirmDowngradeTarget).charAt(0).toUpperCase() + String(confirmDowngradeTarget).slice(1)) : ''}</strong>{' '}
            at the end of your current billing period.
          </div>
          <ul className="mb-2">
            <li>Downgrades take effect at the end of your current billing period.</li>
            <li>Features and limits will change on the next renewal date.</li>
          </ul>
          <div className="text-muted small">Proceed with the downgrade?</div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmDowngrade(false)}>Cancel</Button>
          <Button
            variant="primary"
            disabled={busy || !confirmDowngradeTarget}
            onClick={async () => {
              if (!confirmDowngradeTarget) { setShowConfirmDowngrade(false); return; }
              setShowConfirmDowngrade(false);
              setBusy(true);
              try {
                const tok = (await supabase.auth.getSession()).data.session?.access_token || '';
                const res = await fetch(`${edgeBase}/functions/v1/upgrade-subscription`, {
                  method: 'POST',
                  headers: { 'content-type': 'application/json', Authorization: `Bearer ${tok}` },
                  body: JSON.stringify({ target_plan_id: confirmDowngradeTarget, effective: 'end_of_period', tenant_id: tenantId }),
                });
                const txt = await res.text();
                if (!res.ok) { setError(txt); setBusy(false); return; }
                // Poll until the next renewal reflects the target plan
                await pollBillingUntil((b) => String(b?.next_plan_id || '') === String(confirmDowngradeTarget) && !!b?.next_start_at, 15000, 1000);
                if (typeof onRefresh === 'function') onRefresh();
              } catch (e) {
                setError(e.message || String(e));
              }
              setBusy(false);
            }}
          >
            Confirm
          </Button>
        </Modal.Footer>
      </Modal>
      {/* Confirm Cancel Modal */}
      <Modal show={showConfirmCancel} onHide={() => setShowConfirmCancel(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Cancel Subscription</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-2">
            This will cancel your subscription at the end of the current billing period. You will retain access until then.
          </div>
          <div className="text-muted small">Do you want to continue?</div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmCancel(false)}>Don't cancel</Button>
          <Button
            variant="danger"
            disabled={busy}
            onClick={async () => {
              setShowConfirmCancel(false);
              setBusy(true);
              try {
                const tok = (await supabase.auth.getSession()).data.session?.access_token || '';
                const res = await fetch(`${edgeBase}/functions/v1/cancel-subscription`, {
                  method: 'POST',
                  headers: { 'content-type': 'application/json', Authorization: `Bearer ${tok}` },
                  body: JSON.stringify({ mode: 'end_of_period', tenant_id: tenantId }),
                });
                if (!res.ok) {
                  setError(await res.text());
                } else if (typeof onRefresh === 'function') {
                  onRefresh();
                }
              } catch (e) {
                setError(e.message || String(e));
              }
              setBusy(false);
            }}
          >
            Cancel at period end
          </Button>
        </Modal.Footer>
      </Modal>
      {/* CTA buttons (Join vs Manage) */}
      <div className="mt-3 d-flex flex-wrap gap-2">
        {!canManageSubscription ? (
          <Button variant="success" disabled={busy} onClick={() => setShowPlan(true)}>
            {isTrialPlan ? 'Upgrade Now' : 'Join Today'}
          </Button>
        ) : (
          <Button variant="primary" disabled={busy} onClick={() => setShowManage(true)}>Manage Subscription</Button>
        )}
      </div>
    </div>
  );
}

function Dashboard() {
  const { tenantId, memberships, siteId } = useTenant();
  const [showSaved, setShowSaved] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [showError, setShowError] = useState(false);
  // Enable Billing tab in all environments (including Production)
  const myRole = React.useMemo(() => {
    try { return String((memberships||[]).find(m=>m.tenant_id===tenantId)?.role || '').toLowerCase(); } catch (_) { return ''; }
  }, [memberships, tenantId]);
  const isEditor = myRole === 'editor';
  const canShowTeamTab = !isEditor;
  const showBillingTab = !isEditor;
  const [key, setKey] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const tab = params.get('tab');
    const status = params.get('status');
    if (tab && (tab !== 'billing' || showBillingTab)) return tab;
    if (status && showBillingTab) return 'billing';
    return 'settings';
  });
  const [billingRefreshTick, setBillingRefreshTick] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return (params.get('status') && showBillingTab) ? 1 : 0;
  });
  const [billingPlanId, setBillingPlanId] = useState('');
  const [isProBonoAccount, setIsProBonoAccount] = useState(false);
  const {
    tenantName,
    siteUrl,
    startingUrl,
    doNotCrawlUrls,
    criticalInstructions,
    tone,
    primaryFacts,
    emailNotificationsEnabled,
    notificationRecipients,
    setServerEmailNotificationsEnabled,
    setServerNotificationRecipients,
    settingsReady,
    settingsError,
    hydratedSiteId,
  } = useSettings();

  // invites-auto-accept is handled by TenantContext.refreshMemberships() on mount
  // and on auth state change -- no need to duplicate here.

  async function saveAll() {
    if (!settingsReady || settingsError) {
      setSaveError(settingsError || 'Settings are still loading. Please wait.');
      setShowError(true);
      return;
    }
    try {
      const token = (await supabase.auth.getSession()).data.session?.access_token || '';
      // Resolve selected site for the current tenant
      const selectedSiteId = siteId || ((typeof window !== 'undefined' && tenantId)
        ? (localStorage.getItem(`site_sel_${tenantId}`) || '')
        : '');
      if (!tenantId || !selectedSiteId) {
        setSaveError('Select a Tenant and Site before saving.');
        setShowError(true);
        return;
      }
      if (hydratedSiteId && hydratedSiteId !== selectedSiteId) {
        setSaveError('Settings are out of sync with the selected site. Please wait for settings to reload.');
        setShowError(true);
        return;
      }
      const payload = {
        tenant_name: (tenantName || undefined),
        siteUrl,
        startingUrl,
        doNotCrawlUrls,
        criticalInstructions,
        tone,
        primaryFacts,
        brandColor: brandColorRef.current || undefined,
        tenant_id: tenantId || undefined,
        site_id: selectedSiteId || undefined,
        emailNotificationsEnabled,
        notificationRecipients,
      };
      const res = await fetch(`${edgeBase}/functions/v1/settings-upsert`, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      const txt = await res.text();
      if (!res.ok) throw new Error(txt);
      // Sync server snapshots so subsequent Send Test won't warn
      setServerEmailNotificationsEnabled(!!emailNotificationsEnabled);
      setServerNotificationRecipients(String(notificationRecipients||'').trim());
      setShowSaved(true);
      setShowError(false);
      setSaveError('');
    } catch (e) {
      const msg = e && e.message ? String(e.message) : String(e);
      setSaveError(`Save failed: ${msg}`);
      setShowError(true);
    }
  }
  const brandColorRef = React.useRef(null);
  return (
    <Container className="py-5">
      <ToastContainer position="bottom-end" className="p-3">
        <Toast bg="success" onClose={() => setShowSaved(false)} show={showSaved} delay={2500} autohide>
          <Toast.Body className="text-white">Settings saved.</Toast.Body>
        </Toast>
        <Toast bg="danger" onClose={() => setShowError(false)} show={showError} delay={5000} autohide>
          <Toast.Body className="text-white">{saveError || 'Save failed.'}</Toast.Body>
        </Toast>
      </ToastContainer>
      <div className="d-flex justify-content-between align-items-end mb-3">
        <DashboardTitleWithTenant />
        <div className="d-flex align-items-end gap-2">
          <TenantSwitcherInline />
          <SiteSwitcherInline />
        </div>
      </div>
      {Array.isArray(memberships) && memberships.length === 0 ? (
        <div className="alert alert-warning">No tenants assigned. Please contact your site administrator or owner for access.</div>
      ) : (
      <Tabs
        activeKey={key}
        onSelect={(k) => {
          const next = k || 'settings';
          setKey(next);
          if (next === 'billing' && showBillingTab) setBillingRefreshTick((t) => t + 1);
        }}
        className="mb-3"
      >
        <Tab eventKey="settings" title="Settings">
          <SettingsTab />
        </Tab>
        <Tab eventKey="content" title="Content">
          <ContentTab />
        </Tab>
        <Tab eventKey="myskinny" title="MySkinny" mountOnEnter unmountOnExit>
          <MySkinnyTab planId={billingPlanId} onPlanChange={setBillingPlanId} />
        </Tab>
        {showBillingTab ? (
          <Tab eventKey="billing" title="Billing" mountOnEnter unmountOnExit>
            <BillingTab
              refreshTick={billingRefreshTick}
              onRefresh={() => setBillingRefreshTick((t) => t + 1)}
              onBillingInfo={(info) => setIsProBonoAccount(!!info?.isProBono)}
            />
          </Tab>
        ) : null}
        <Tab eventKey="share" title="Share" mountOnEnter unmountOnExit>
          <ShareTab />
        </Tab>
        <Tab eventKey="usage" title="Usage" mountOnEnter unmountOnExit>
          <UsageTab />
        </Tab>
        <Tab eventKey="history" title="History" mountOnEnter unmountOnExit>
          <HistoryTab />
        </Tab>
        <Tab eventKey="sites" title="Sites" mountOnEnter unmountOnExit>
          <SitesTab planRefreshTick={billingRefreshTick} />
        </Tab>
        {canShowTeamTab ? (
        <Tab eventKey="team" title="Team" mountOnEnter unmountOnExit>
          <TeamTab planId={billingPlanId} isProBono={isProBonoAccount} />
          </Tab>
        ) : null}
        <Tab eventKey="analysis" title="Analysis" mountOnEnter unmountOnExit>
          <UserAnalysisTab />
        </Tab>
      </Tabs>
      )}
      {(['settings','content','myskinny'].includes(key)) ? (
        <div className="d-flex flex-column align-items-end mt-3 gap-2">
          {settingsError ? (
            <div className="alert alert-warning py-2 px-3 mb-0 small">{settingsError}</div>
          ) : null}
          <Button variant="primary" onClick={saveAll} disabled={!settingsReady || !!settingsError}>
            {settingsError ? 'Save' : settingsReady ? 'Save' : 'Loading\u2026'}
          </Button>
        </div>
      ) : null}
    </Container>
  );
}

function DashboardTitleWithTenant() {
  const { tenantName } = useTenant();
  return (
    <div className="d-flex align-items-baseline gap-2">
      <h1 className="h3 fw-bold m-0">Dashboard</h1>
      {tenantName ? <span className="text-muted small">· {tenantName}</span> : null}
    </div>
  );
}

export default Dashboard;


